// =============================================================================
// UNIFIED DATA ACCESS LAYER - TypeScript Migration
// SINGLE SOURCE OF TRUTH - Now using Supabase Cloud Storage
// =============================================================================

/**
 * Note: This file has been partially migrated to TypeScript.
 * The original orders.js file is very large (>25k tokens).
 * This TypeScript file provides type definitions and re-exports from the JS file.
 * TODO: Complete full migration of orders.js to TypeScript in a separate batch.
 */

import { logger } from '@/utils/logger'
import { isOperationsOrderComplete } from '../../shared/operationsOrderStatus'
import { updateOrderSupabase, claimOrderAtomic, type ClaimPayload } from './supabase/orders'
import { notifyPartnerOfChangeOrder } from './notifications'
import { getPartners } from './partners'
import { 
  getAllPartnerIdentifiers,
  isOrderTargetedToPartner,
  isOrderClaimedByPartner
} from '../services/PartnerIdentityService'

const log = logger.module('Orders')

// =============================================================================
// PARTNER IDENTITY RESOLUTION
// Now delegated to PartnerIdentityService for single source of truth
// Re-export for backward compatibility
// =============================================================================

export { getAllPartnerIdentifiers, isOrderTargetedToPartner, isOrderClaimedByPartner }

// =============================================================================
// AUTO-SYNC CONTROL: Module-level flag to disable fire-and-forget sync
// Used by releaseToPartners to prevent race conditions during explicit sync
// =============================================================================
let skipAutoSync = false

export function setSkipAutoSync(skip: boolean) {
  skipAutoSync = skip
  log.debug('[setSkipAutoSync]', skip ? 'DISABLED' : 'ENABLED')
}

// Helper to strip large base64 image data from objects before saving to Supabase
// This prevents PostgreSQL timeout errors (57014) caused by massive payloads
function stripImageData(obj: any): any {
  if (!obj) return obj;
  if (Array.isArray(obj)) {
    return obj.map(item => stripImageData(item));
  }
  if (typeof obj === 'object') {
    const stripped: any = {};
    for (const [key, value] of Object.entries(obj)) {
      if (key === 'imageData' || key === 'image' || key === 'dataUrl') {
        continue;
      }
      if (typeof value === 'string' && value.startsWith('data:image/')) {
        continue;
      }
      stripped[key] = stripImageData(value);
    }
    return stripped;
  }
  return obj;
}

// =============================================================================
// TYPES - Production Stages
// =============================================================================

export interface ProductionStage {
  id: string
  label: string
  clientLabel: string
  order: number
}

export interface ClientTrackingPhase {
  id: string
  label: string
  description: string
  order: number
  conditional?: boolean
  subSteps: Array<{
    id: string
    label: string
  }>
}

export interface CompletedSubSteps {
  [key: string]: boolean
}

export interface CurrentTrackingPhase {
  phaseId: string | null
  subStepId: string | null
  phaseIndex: number
}

export interface ProductionHealthMetrics {
  atRiskCount: number
  onTrackCount: number
  overdueCount: number
  totalDaysInProduction: number
}

export interface ProductionRiskStatus {
  atRisk: boolean
  daysOverdue: number
  reason: string | null
}

export interface OrderStatus {
  cutting?: boolean
  cuttingDate?: string
  holesDrilled?: boolean
  readyForPaint?: boolean
  readyForPaintDate?: string
  paintComplete?: boolean
  paintCompleteDate?: string
  qcPassed?: boolean
  qcPassedDate?: string
  packed?: boolean
  packedDate?: string
  shipped?: boolean
  shippedDate?: string
  delivered?: boolean
  deliveredDate?: string
  installComplete?: boolean
  installCompleteDate?: string
  qcRequested?: boolean
  outForDelivery?: boolean
}

// Import adjustment code type for change orders
import type { AdjustmentCodeType } from '../types/priceAdjustment'

// Change Order interface for tracking modifications to orders
export interface ChangeOrder {
  id: string                   // "CO-1", "CO-2", etc.
  version: number              // 1, 2, 3...
  
  // ADJUSTMENT CODE - Required justification for the price change
  adjustmentCode: AdjustmentCodeType  // Standardized code (e.g., 'CLIENT_REVISION', 'SITE_CONDITION')
  reason: string               // Additional details about why the change was needed
  
  deltaCutlist: DeltaCutlistPiece[]  // New/modified pieces only
  costDelta: number            // Client pays this additional amount
  payoutDelta: number          // Partner receives this additional amount
  status: 'pending_approval' | 'approved' | 'invoiced' | 'paid' | 'released' | 'rejected'
  createdAt: string
  createdBy: string            // Designer who created the change order
  createdByRole?: 'sales' | 'designer' | 'manager' | 'admin'
  approvedBy?: string          // Admin/Finance who approved
  approvedAt?: string
  invoiceId?: string           // Stripe/invoice ID for payment tracking
  paidAt?: string              // When client paid for change order
  releasedAt?: string          // When sent back to partner
  releasedToPartnerId?: string // Which partner received it
  releasedToPartnerName?: string
  rushOrder: boolean           // Priority flag
  notes?: string               // Additional notes
}

// Delta cutlist piece for change orders
export interface DeltaCutlistPiece {
  id: string
  name: string
  quantity: number
  dimensions: {
    length?: number | string
    width?: number | string
    thickness?: number | string
  }
  material?: string
  notes?: string
  isReplacement?: boolean      // True if replacing existing piece
  replacesOriginalId?: string  // ID of piece being replaced
}

export interface Order {
  orderId?: string
  id?: string
  orderNumber?: string
  status?: string
  fabricationStatus?: string
  claimedByPartner?: string
  claimedByPartnerAt?: string
  claimedByPartnerName?: string
  deployedAt?: string
  fabricationStartedAt?: string
  fabricationCompletedAt?: string
  dueDate?: string
  productionStage?: string
  productionHistory?: Array<{
    stageId: string
    stageLabel: string
    timestamp: string
    notes: string
  }>
  status_obj?: OrderStatus
  materialsOrdered?: boolean
  materialsOrderedDate?: string
  releasedToProduction?: boolean
  pipeline?: {
    designApproved?: boolean
    designApprovedDate?: string
    cutlistReceived?: boolean
    depositPaid?: boolean
    designNotified?: boolean
  }
  clientSignedAt?: string
  documents?: {
    purchaseOrder?: any
  }
  installation?: {
    sitePrepped?: boolean
    installed?: boolean
    walkthroughComplete?: boolean
    approved?: boolean
  }
  changeOrders?: ChangeOrder[]
  client?: {
    name?: string
  }
  clientName?: string
  config?: string
  lockedSpecs?: {
    config?: string
  }
  [key: string]: any
}

// =============================================================================
// CONSTANTS
// =============================================================================

export const PRODUCTION_STAGES: ProductionStage[] = [
  { id: 'design', label: 'Design Approval', clientLabel: 'Design Finalized', order: 1 },
  { id: 'materials', label: 'Materials Ordered', clientLabel: 'In Production', order: 2 },
  { id: 'fabrication', label: 'Fabrication', clientLabel: 'In Production', order: 3 },
  { id: 'finishing', label: 'Finishing', clientLabel: 'In Production', order: 4 },
  { id: 'qc', label: 'Quality Control', clientLabel: 'Quality Check', order: 5 },
  { id: 'crating', label: 'Crating', clientLabel: 'Preparing for Delivery', order: 6 },
  { id: 'shipping', label: 'Shipping', clientLabel: 'On the Way', order: 7 },
  { id: 'delivery', label: 'Delivered', clientLabel: 'Delivered', order: 8 },
  { id: 'install', label: 'Installation', clientLabel: 'Installation Complete', order: 9 }
]

// KEY FUNCTIONS
// =============================================================================

/**
 * Get all orders
 * @deprecated Use getAllOrdersAsync for Supabase-first
 * This function returns empty array - Supabase is the source of truth
 */
export function getAllOrders(): Order[] {
  log.warn('[DEPRECATED] getAllOrders() called - use getAllOrdersAsync() instead')
  return []
}

/**
 * SUPABASE-FIRST: Get all orders from Supabase
 * NO LOCALSTORAGE FALLBACK - Supabase is the source of truth
 */
export async function getAllOrdersAsync(): Promise<Order[]> {
  try {
    const { OrdersService } = await import('../services/cloudDataService')
    const result = await OrdersService.getAll()
    
    if (result.error || !result.data) {
      log.error('[getAllOrdersAsync] Supabase fetch failed:', result.error)
      // NO FALLBACK: Return empty array, don't use stale localStorage
      return []
    }
    
    // Supabase is source of truth - no localStorage cache needed
    return result.data as Order[]
  } catch (err) {
    log.error('[getAllOrdersAsync] Failed:', err)
    // NO FALLBACK: Return empty array, don't use stale localStorage
    return []
  }
}

/**
 * Get order by ID (from localStorage - use getOrderByIdAsync for Supabase-first)
 */
export function getOrderById(orderId: string): Order | undefined {
  const orders = getAllOrders()
  return orders.find(o => o.orderId === orderId || o.id === orderId || o.orderNumber === orderId)
}

// isOrderClaimedByPartner is now imported and re-exported from PartnerIdentityService

/**
 * SUPABASE-FIRST: Get order by ID from cloud
 * NO LOCALSTORAGE FALLBACK - Supabase is the source of truth
 * Use this for all operations that need current state (withdraw, recall, claim, etc.)
 */
export async function getOrderByIdAsync(orderId: string): Promise<Order | undefined> {
  try {
    const { OrdersService } = await import('../services/cloudDataService')
    const result = await OrdersService.getById(orderId)
    if (result.data) {
      return result.data as Order
    }
    // Order not found in Supabase - return undefined (don't fall back to stale cache)
    return undefined
  } catch (err) {
    log.error('getOrderByIdAsync: Supabase fetch failed', { orderId, err })
    // NO FALLBACK: Return undefined, don't use stale localStorage
    return undefined
  }
}

/**
 * Save order - Syncs directly to Supabase (source of truth)
 * 
 * @param order - The order to save
 * @param options - Optional settings
 * @param options.skipSupabaseSync - If true, skip Supabase sync (caller will handle it)
 */
export function saveOrder(order: Order, options?: { skipSupabaseSync?: boolean }): Order {
  order.updatedAt = new Date().toISOString()

  const orderId = order.orderId || order.id
  if (orderId && !options?.skipSupabaseSync && !skipAutoSync) {
    // Fire-and-forget async sync (don't block the UI)
    syncOrderToSupabase(order).catch(err => {
      log.error('[saveOrder] Supabase sync failed:', { orderId, error: err })
      console.error('⚠️ SUPABASE SYNC FAILED for order:', orderId, err)
    })
  }
  
  return order
}

/**
 * Internal function to sync order to Supabase
 * Called automatically by saveOrder()
 * 
 * CRITICAL: This function preserves claim fields from Supabase if the order is claimed.
 * This prevents stale local data from overwriting fresh claim status.
 */
async function syncOrderToSupabase(order: Order): Promise<void> {
  const orderId = order.orderId || order.id
  if (!orderId) return
  
  try {
    // CLAIM FIELD PRESERVATION: Fetch current order from Supabase to check claim status
    // If order is claimed in Supabase, preserve those fields (cloud wins for claim data)
    const { OrdersService } = await import('../services/cloudDataService')
    const currentResult = await OrdersService.getById(orderId)
    const currentOrder = currentResult.data
    
    // Determine which claim/fabrication fields to use
    // If Supabase has a released/claimed/in_progress order, ALWAYS preserve those fields
    // CRITICAL: 'ready' must be protected or auto-save can reset orders back to draft!
    const protectedStatuses = ['ready', 'claimed', 'in_progress', 'paused', 'qc_requested', 'revision_requested', 'qc_approved', 'packing', 'ready_to_ship', 'shipped', 'complete']
    const isProtectedInSupabase = currentOrder && protectedStatuses.includes(currentOrder.fabricationStatus)
    
    // Use Supabase values for fabrication fields if order has protected status there
    const fabricationFields = isProtectedInSupabase ? {
      fabricationStatus: currentOrder.fabricationStatus,
      claimedByPartner: currentOrder.claimedByPartner,
      claimedByPartnerName: currentOrder.claimedByPartnerName,
      claimedByPartnerAt: currentOrder.claimedByPartnerAt,
      claimedByPartnerLocation: currentOrder.claimedByPartnerLocation,
    } : {
      fabricationStatus: order.fabricationStatus,
      // SAFETY NET: Never send non-UUID to claimed_by_partner (FK constraint)
      claimedByPartner: /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(order.claimedByPartner || '') ? order.claimedByPartner : null,
      claimedByPartnerName: order.claimedByPartnerName,
      claimedByPartnerAt: order.claimedByPartnerAt,
      claimedByPartnerLocation: order.claimedByPartnerLocation,
    }
    
    if (isProtectedInSupabase && order.fabricationStatus !== currentOrder.fabricationStatus) {
      log.warn('[syncOrderToSupabase] ⚠️ STATUS GUARD: Preserving Supabase fabrication status', {
        orderId,
        localStatus: order.fabricationStatus,
        supabaseStatus: currentOrder.fabricationStatus,
        claimedBy: currentOrder.claimedByPartner
      })
    }
    
    // COMPREHENSIVE SYNC: Include ALL fields that need persistence
    const result = await updateOrderSupabase(orderId, {
      // Core fields
      orderId: order.orderId,
      client: order.client,
      beds: order.beds || (order.lockedSpecs as any)?.placedBeds,
      pricing: order.pricing,
      payment: order.payment,
      status: order.status,
      // Design fields - COMPLETE (using 'as any' for dynamic lockedSpecs properties)
      lockedSpecs: {
        ...(order.lockedSpecs || {}),
        // Ensure ALL design fields are in lockedSpecs
        placedBeds: order.beds || (order.lockedSpecs as any)?.placedBeds,
        bedConfigs: (order as any).bedConfigs || (order.lockedSpecs as any)?.bedConfigs,
        roomDimensions: order.roomDimensions || (order.lockedSpecs as any)?.roomDimensions,
        woodType: (order as any).wood || (order as any).woodType || (order.lockedSpecs as any)?.woodType,
        finish: order.finish || (order.lockedSpecs as any)?.finish,
        guardrailStyle: (order as any).guardrailStyle || (order.lockedSpecs as any)?.guardrailStyle,
        guardrailCoverage: (order as any).guardrailCoverage || (order.lockedSpecs as any)?.guardrailCoverage,
        guardrailTemplate: (order as any).guardrailTemplate || (order.lockedSpecs as any)?.guardrailTemplate,
        guardrailConfigs: (order as any).guardrailConfigs || (order.lockedSpecs as any)?.guardrailConfigs,
        discount: (order as any).discount || (order.lockedSpecs as any)?.discount,
        deliveryZone: (order as any).deliveryZone || (order.lockedSpecs as any)?.deliveryZone,
        wantsInstall: (order as any).wantsInstall ?? (order.lockedSpecs as any)?.wantsInstall,
        addOns: order.addOns || (order.lockedSpecs as any)?.addOns,
        renders: (order as any).renders || (order.lockedSpecs as any)?.renders,
        floorPlanCapture: (order as any).floorPlanCapture || (order.lockedSpecs as any)?.floorPlanCapture
      },
      roomDimensions: order.roomDimensions || (order.lockedSpecs as any)?.roomDimensions,
      woodType: order.wood || order.woodType,
      finish: order.finish,
      addOns: order.addOns,
      // Pipeline fields
      pipeline: order.pipeline,
      pipelineStage: order.pipelineStage,
      // Fabrication fields - use preserved values if order is claimed in Supabase
      ...fabricationFields,
      // Non-claim fabrication fields (always from local)
      releasedToProduction: order.releasedToProduction,
      releasedToProductionAt: order.releasedToProductionAt,
      releasedToPartnersAt: order.releasedToPartnersAt,
      releaseMode: order.releaseMode,
      targetPartnerId: order.targetPartnerId,
      targetPartnerName: order.targetPartnerName,
      // Timestamps
      updatedAt: order.updatedAt
    } as any)
    
    if (result.error) {
      log.error('[syncOrderToSupabase] FAILED:', { orderId, error: result.error })
    } else {
      log.debug('[syncOrderToSupabase] ✅ Synced:', orderId)
    }
  } catch (err) {
    log.error('[syncOrderToSupabase] Exception:', { orderId, err })
    throw err
  }
}

/**
 * Check if order is visible in operations
 */
export function isOperationsVisible(order: Order): boolean {
  // Only show orders that have started production
  return !!(
    order.releasedToProduction ||
    order.pipeline?.designApproved ||
    order.clientSignedAt ||
    order.fabricationStatus === 'claimed' ||
    order.fabricationStatus === 'in_progress' ||
    order.materialsOrdered
  )
}

/**
 * Get production stage from order
 */
export function getProductionStage(order: Order): string | null {
  if (!order) return null

  // If order has explicit productionStage, use it
  if (order.productionStage) {
    return order.productionStage
  }

  const status = order.status_obj || {}
  const fabStatus = order.fabricationStatus

  // Work backwards from most complete stages
  if (status.delivered || status.installComplete) return 'install'
  if (status.shipped) return 'delivery'
  if (fabStatus === 'ready_to_ship' || status.packed) return 'shipping'
  if (fabStatus === 'packing') return 'crating'
  if (fabStatus === 'qc_approved' || fabStatus === 'qc_requested' || status.qcPassed) return 'qc'
  if (status.paintComplete || (fabStatus === 'in_progress' && status.readyForPaint)) return 'finishing'
  if (fabStatus === 'in_progress' || fabStatus === 'claimed' || status.cutting || status.holesDrilled) return 'fabrication'
  if (order.materialsOrdered || order.documents?.purchaseOrder) return 'materials'
  if (order.releasedToProduction || order.pipeline?.designApproved || order.clientSignedAt) return 'design'

  return null
}

/**
 * Get production stage index
 */
export function getProductionStageIndex(order: Order): number {
  const stageId = getProductionStage(order)
  if (!stageId) return 0
  const stage = PRODUCTION_STAGES.find(s => s.id === stageId)
  return stage?.order || 0
}

/**
 * Get production progress as percentage
 */
export function getProductionProgress(order: Order): number {
  const stageIndex = getProductionStageIndex(order)
  return Math.round((stageIndex / PRODUCTION_STAGES.length) * 100)
}

/**
 * Update production stage
 */
export function updateProductionStage(orderId: string, stageId: string, notes = ''): {
  success: boolean
  error?: string
  order?: Order
  stage?: ProductionStage
} {
  const order = getOrderById(orderId)
  if (!order) return { success: false, error: 'Order not found' }

  const stage = PRODUCTION_STAGES.find(s => s.id === stageId)
  if (!stage) return { success: false, error: 'Invalid stage' }

  const now = new Date().toISOString()

  // Set explicit production stage
  order.productionStage = stageId

  // Initialize production history if not exists
  if (!order.productionHistory) {
    order.productionHistory = []
  }

  // Log the stage transition
  order.productionHistory.push({
    stageId,
    stageLabel: stage.label,
    timestamp: now,
    notes
  })

  // Update corresponding status flags for backwards compatibility
  if (!order.status_obj) order.status_obj = {}

  switch (stageId) {
    case 'design':
      order.pipeline = { ...order.pipeline, designApproved: true, designApprovedDate: now }
      break
    case 'materials':
      order.materialsOrdered = true
      order.materialsOrderedDate = now
      break
    case 'fabrication':
      order.status_obj.cutting = true
      order.status_obj.cuttingDate = now
      if (!order.fabricationStatus || order.fabricationStatus === 'ready') {
        order.fabricationStatus = 'in_progress'
        order.fabricationStartedAt = now
      }
      break
    case 'finishing':
      order.status_obj.readyForPaint = true
      order.status_obj.readyForPaintDate = now
      break
    case 'qc':
      if (!order.status_obj.paintComplete) {
        order.status_obj.paintComplete = true
        order.status_obj.paintCompleteDate = now
      }
      break
    case 'crating':
      order.status_obj.qcPassed = true
      order.status_obj.qcPassedDate = now
      order.fabricationStatus = 'packing'
      break
    case 'shipping':
      order.status_obj.packed = true
      order.status_obj.packedDate = now
      order.fabricationStatus = 'ready_to_ship'
      break
    case 'delivery':
      order.status_obj.shipped = true
      order.status_obj.shippedDate = now
      order.fabricationStatus = 'complete'
      break
    case 'install':
      order.status_obj.delivered = true
      order.status_obj.deliveredDate = now
      order.status_obj.installComplete = true
      order.status_obj.installCompleteDate = now
      break
  }

  order.updatedAt = now
  saveOrder(order)

  // CRITICAL: Sync fabricationStatus and productionStage to Supabase
  // This ensures Millworks portal and other views stay in sync
  updateOrderSupabase(orderId, {
    productionStage: order.productionStage,
    fabricationStatus: order.fabricationStatus,
    statusObj: order.status_obj,
    updatedAt: now
  }).catch(err => {
    log.error('[updateProductionStage] Supabase sync failed:', { orderId, error: err })
  })

  return { success: true, order, stage }
}

/**
 * Get orders by production stage
 */
export function getOrdersByProductionStage(stageId: string): Order[] {
  const orders = getAllOrders()
  return orders.filter(order => {
    // Only include orders that have started production
    if (!isOperationsVisible(order)) return false
    const currentStage = getProductionStage(order)
    return currentStage === stageId
  })
}

/**
 * Check if production is at risk
 */
export function isProductionAtRisk(order: Order): ProductionRiskStatus {
  if (isOperationsOrderComplete(order) || !order?.dueDate) return { atRisk: false, daysOverdue: 0, reason: null }

  const dueDate = new Date(order.dueDate)
  const now = new Date()
  const daysUntilDue = Math.ceil((dueDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24))

  // Already overdue
  if (daysUntilDue < 0) {
    return {
      atRisk: true,
      daysOverdue: Math.abs(daysUntilDue),
      reason: `${Math.abs(daysUntilDue)} days overdue`
    }
  }

  // Check if progress matches timeline
  const stageIndex = getProductionStageIndex(order)
  const expectedStageByNow = Math.ceil((1 - (daysUntilDue / 30)) * PRODUCTION_STAGES.length)

  if (stageIndex < expectedStageByNow - 1) {
    return {
      atRisk: true,
      daysOverdue: 0,
      reason: `Behind schedule - should be at stage ${expectedStageByNow}`
    }
  }

  // Due within 3 days but not in final stages
  if (daysUntilDue <= 3 && stageIndex < 7) {
    const currentStageLabel = PRODUCTION_STAGES.find(s => s.id === getProductionStage(order))?.label || 'Unknown'
    return {
      atRisk: true,
      daysOverdue: 0,
      reason: `Due in ${daysUntilDue} days, only at ${currentStageLabel}`
    }
  }

  return { atRisk: false, daysOverdue: 0, reason: null }
}

/**
 * Calculate days in current stage
 */
export function getDaysInCurrentStage(order: Order): number | null {
  if (!order.productionHistory || order.productionHistory.length === 0) {
    // Fall back to fabrication started date or claimed date
    const startDate = order.fabricationStartedAt || order.claimedByPartnerAt || order.deployedAt
    if (!startDate) return null
    return Math.floor((Date.now() - new Date(startDate).getTime()) / (1000 * 60 * 60 * 24))
  }

  const lastEntry = order.productionHistory[order.productionHistory.length - 1]
  if (!lastEntry?.timestamp) return null

  return Math.floor((Date.now() - new Date(lastEntry.timestamp).getTime()) / (1000 * 60 * 60 * 24))
}

// =============================================================================
// CLIENT TRACKING PHASES - For customer-facing order tracking
// =============================================================================

export interface TrackingSubStep {
  id: string
  label: string
  description?: string
}

export interface TrackingPhase {
  id: string
  label: string
  icon?: string
  subSteps: TrackingSubStep[]
  conditional?: boolean
}

// =============================================================================
// TRACKING STATUS - Supabase Source of Truth
// This structure is stored in orders.tracking_status JSONB column
// =============================================================================

export interface TrackingStatusStep {
  id: string
  completed: boolean
  completedAt?: string
  completedBy?: string
  notes?: string
}

export interface FabricationQueuePosition {
  position: number           // 1 = next up, 2 = second in line, etc.
  ordersAhead: Array<{       // Names of orders ahead in queue
    orderId: string
    clientName: string
  }>
  estimatedStartDate?: string
}

export interface TrackingStatus {
  // Phase 1: Order & Payment
  orderCreated: TrackingStatusStep
  depositPaid: TrackingStatusStep
  
  // Phase 2: Design
  designStarted: TrackingStatusStep
  designVerbalApproval: TrackingStatusStep
  designSignedApproval: TrackingStatusStep  // Formal signature on approval form
  
  // Phase 3: Pre-Production
  materialsOrdered: TrackingStatusStep
  materialsReceived: TrackingStatusStep
  
  // Phase 4: Fabrication
  inFabricationQueue: TrackingStatusStep
  fabricationQueuePosition?: FabricationQueuePosition
  fabricationStarted: TrackingStatusStep
  fabricationComplete: TrackingStatusStep
  
  // Phase 5: Finishing & QC
  finishingStarted: TrackingStatusStep
  finishingComplete: TrackingStatusStep
  qualityCheckPassed: TrackingStatusStep
  
  // Phase 6: Shipping
  cratingComplete: TrackingStatusStep
  shipped: TrackingStatusStep
  inTransit: TrackingStatusStep
  
  // Phase 7: Delivery & Installation
  delivered: TrackingStatusStep
  installationScheduled: TrackingStatusStep
  installationComplete: TrackingStatusStep
  
  // Metadata
  currentPhase: string       // Current high-level phase
  currentStep: string        // Current specific step
  lastUpdated: string
  lastUpdatedBy?: string
}

// Default tracking status for new orders
export function createDefaultTrackingStatus(): TrackingStatus {
  const defaultStep = (): TrackingStatusStep => ({ id: '', completed: false })
  return {
    orderCreated: { ...defaultStep(), id: 'orderCreated', completed: true, completedAt: new Date().toISOString() },
    depositPaid: { ...defaultStep(), id: 'depositPaid' },
    designStarted: { ...defaultStep(), id: 'designStarted' },
    designVerbalApproval: { ...defaultStep(), id: 'designVerbalApproval' },
    designSignedApproval: { ...defaultStep(), id: 'designSignedApproval' },
    materialsOrdered: { ...defaultStep(), id: 'materialsOrdered' },
    materialsReceived: { ...defaultStep(), id: 'materialsReceived' },
    inFabricationQueue: { ...defaultStep(), id: 'inFabricationQueue' },
    fabricationStarted: { ...defaultStep(), id: 'fabricationStarted' },
    fabricationComplete: { ...defaultStep(), id: 'fabricationComplete' },
    finishingStarted: { ...defaultStep(), id: 'finishingStarted' },
    finishingComplete: { ...defaultStep(), id: 'finishingComplete' },
    qualityCheckPassed: { ...defaultStep(), id: 'qualityCheckPassed' },
    cratingComplete: { ...defaultStep(), id: 'cratingComplete' },
    shipped: { ...defaultStep(), id: 'shipped' },
    inTransit: { ...defaultStep(), id: 'inTransit' },
    delivered: { ...defaultStep(), id: 'delivered' },
    installationScheduled: { ...defaultStep(), id: 'installationScheduled' },
    installationComplete: { ...defaultStep(), id: 'installationComplete' },
    currentPhase: 'order_payment',
    currentStep: 'orderCreated',
    lastUpdated: new Date().toISOString()
  }
}

// Client-facing tracking phases (what customers see)
export const CLIENT_TRACKING_PHASES: TrackingPhase[] = [
  {
    id: 'order_payment',
    label: 'Order & Payment',
    icon: '✓',
    subSteps: [
      { id: 'orderCreated', label: 'Order Created' },
      { id: 'depositPaid', label: 'Deposit Paid' }
    ]
  },
  {
    id: 'design',
    label: 'Design Approval',
    icon: '✏️',
    subSteps: [
      { id: 'designStarted', label: 'Design Started' },
      { id: 'designVerbalApproval', label: 'Design Reviewed' },
      { id: 'designSignedApproval', label: 'Design Approved & Signed' }
    ]
  },
  {
    id: 'pre_production',
    label: 'Pre-Production',
    icon: '📋',
    subSteps: [
      { id: 'materialsOrdered', label: 'Materials Ordered' },
      { id: 'materialsReceived', label: 'Materials Received' },
      { id: 'inFabricationQueue', label: 'In Fabrication Queue' }
    ]
  },
  {
    id: 'production',
    label: 'In Production',
    icon: '🔨',
    subSteps: [
      { id: 'fabricationStarted', label: 'Fabrication Started' },
      { id: 'fabricationComplete', label: 'Fabrication Complete' },
      { id: 'finishingStarted', label: 'Finishing' },
      { id: 'finishingComplete', label: 'Finishing Complete' },
      { id: 'qualityCheckPassed', label: 'Quality Check Passed' }
    ]
  },
  {
    id: 'shipping',
    label: 'Shipping',
    icon: '📦',
    subSteps: [
      { id: 'cratingComplete', label: 'Crating Complete' },
      { id: 'shipped', label: 'Shipped' },
      { id: 'inTransit', label: 'In Transit' }
    ]
  },
  {
    id: 'installation',
    label: 'Installation',
    icon: '🏠',
    subSteps: [
      { id: 'delivered', label: 'Delivered' },
      { id: 'installationScheduled', label: 'Installation Scheduled' },
      { id: 'installationComplete', label: 'Complete' }
    ]
  }
]

// Get current phase and step from tracking status
export function getCurrentPhaseFromTrackingStatus(trackingStatus: TrackingStatus): { phaseId: string, stepId: string, phaseLabel: string, stepLabel: string } {
  if (!trackingStatus) {
    return { phaseId: 'order_payment', stepId: 'orderCreated', phaseLabel: 'Order & Payment', stepLabel: 'Order Created' }
  }
  
  const phase = CLIENT_TRACKING_PHASES.find(p => p.id === trackingStatus.currentPhase)
  const step = phase?.subSteps.find(s => s.id === trackingStatus.currentStep)
  
  return {
    phaseId: trackingStatus.currentPhase || 'order_payment',
    stepId: trackingStatus.currentStep || 'orderCreated',
    phaseLabel: phase?.label || 'Processing',
    stepLabel: step?.label || 'In Progress'
  }
}

/**
 * Get client phase by ID
 */
export function getClientPhaseById(phaseId: string): TrackingPhase | undefined {
  return CLIENT_TRACKING_PHASES.find(p => p.id === phaseId)
}

/**
 * Get completed sub-steps for an order
 */
export function getCompletedSubSteps(order: Order): Record<string, boolean> {
  const completed: Record<string, boolean> = {}
  const stage = getProductionStage(order)
  const stageIndex = getProductionStageIndex(order)
  
  // Mark sub-steps as complete based on production stage
  if (stageIndex >= 1) {
    completed['payment_received'] = true
    completed['order_created'] = true
  }
  if (stageIndex >= 1) {
    completed['design_started'] = true
    completed['design_review'] = true
    completed['design_approved'] = true
  }
  if (stageIndex >= 2) completed['materials_ordered'] = true
  if (stageIndex >= 3) completed['fabrication'] = true
  if (stageIndex >= 4) completed['finishing'] = true
  if (stageIndex >= 5) completed['quality_check'] = true
  if (stageIndex >= 6) completed['crating'] = true
  if (stageIndex >= 7) {
    completed['shipped'] = true
    completed['in_transit'] = true
  }
  if (stageIndex >= 8) completed['delivered'] = true
  if (stageIndex >= 9) {
    completed['installed'] = true
    completed['complete'] = true
  }
  
  return completed
}

/**
 * Get current tracking phase for an order
 */
export function getCurrentTrackingPhase(order: Order): { phaseId: string; subStepId: string } {
  const stage = getProductionStage(order)
  
  // Map production stages to tracking phases
  const stageToPhase: Record<string, { phaseId: string; subStepId: string }> = {
    'design': { phaseId: 'design', subStepId: 'design_review' },
    'materials': { phaseId: 'production', subStepId: 'materials_ordered' },
    'fabrication': { phaseId: 'production', subStepId: 'fabrication' },
    'finishing': { phaseId: 'production', subStepId: 'finishing' },
    'qc': { phaseId: 'production', subStepId: 'quality_check' },
    'crating': { phaseId: 'shipping', subStepId: 'crating' },
    'shipping': { phaseId: 'shipping', subStepId: 'in_transit' },
    'delivery': { phaseId: 'installation', subStepId: 'delivered' },
    'install': { phaseId: 'installation', subStepId: 'installed' }
  }
  
  return stageToPhase[stage || ''] || { phaseId: 'order_confirmed', subStepId: 'order_created' }
}

/**
 * Get production stage by ID
 */
export function getProductionStageById(stageId: string): ProductionStage | undefined {
  return PRODUCTION_STAGES.find(s => s.id === stageId)
}

/**
 * Get client-facing stage label
 */
export function getClientStageLabel(order: Order): string {
  const stage = getProductionStage(order)
  const stageObj = PRODUCTION_STAGES.find(s => s.id === stage)
  return stageObj?.clientLabel || 'Processing'
}

/**
 * Get internal stage label
 */
export function getInternalStageLabel(order: Order): string {
  const stage = getProductionStage(order)
  const stageObj = PRODUCTION_STAGES.find(s => s.id === stage)
  return stageObj?.label || 'Unknown'
}

/**
 * Get production stage counts
 */
export function getProductionStageCounts(): Record<string, number> {
  const orders = getAllOrders()
  const counts: Record<string, number> = {}
  
  for (const stage of PRODUCTION_STAGES) {
    counts[stage.id] = 0
  }
  
  for (const order of orders) {
    const stage = getProductionStage(order)
    if (stage && counts[stage] !== undefined) {
      counts[stage]++
    }
  }
  
  return counts
}

/**
 * Get production health metrics
 */
export function getProductionHealthMetrics(): {
  totalOrders: number
  totalInProduction: number
  onTrack: number
  onTrackCount: number
  atRisk: number
  atRiskCount: number
  overdue: number
  overdueCount: number
  averageProgress: number
  bottlenecks: Array<{ id: string; label: string }>
  stageCounts: Array<{ id: string; label: string; count: number }>
} {
  const orders = getAllOrders().filter(o => o.status === 'in_production' || o.status === 'approved')
  
  let onTrack = 0
  let atRisk = 0
  let overdue = 0
  let totalProgress = 0
  
  for (const order of orders) {
    const risk = isProductionAtRisk(order)
    if (risk.atRisk) {
      if (risk.daysOverdue > 0) {
        overdue++
      } else {
        atRisk++
      }
    } else {
      onTrack++
    }
    totalProgress += getProductionProgress(order)
  }

  // Get stage counts for bottleneck detection
  const stageCountsObj = getProductionStageCounts()
  
  // Convert to array format for UI consumption
  const stageCounts = PRODUCTION_STAGES.map(stage => ({
    id: stage.id,
    label: stage.label,
    count: stageCountsObj[stage.id] || 0
  }))
  
  // Identify bottlenecks (stages with more than 3 orders waiting)
  const bottlenecks = stageCounts
    .filter(stage => stage.count > 3)
    .map(stage => ({ id: stage.id, label: stage.label }))
  
  return {
    totalOrders: orders.length,
    totalInProduction: orders.length,
    onTrack,
    onTrackCount: onTrack,
    atRisk,
    atRiskCount: atRisk,
    overdue,
    overdueCount: overdue,
    averageProgress: orders.length > 0 ? Math.round(totalProgress / orders.length) : 0,
    bottlenecks,
    stageCounts
  }
}

/**
 * Get orders in design stage
 */
export function getDesignOrders(): Order[] {
  return getAllOrders().filter(order => {
    const stage = getProductionStage(order)
    return stage === 'design'
  })
}

/**
 * Get orders in operations/fabrication stage
 */
export function getOperationsOrders(): Order[] {
  return getAllOrders().filter(order => isOperationsVisible(order))
}

/**
 * Delete an order by ID
 */
export function deleteOrder(orderId: string): boolean {
  try {
    const orders = getAllOrders()
    const filtered = orders.filter(o => o.id !== orderId)

    if (filtered.length === orders.length) {
      return false // Order not found
    }

    // Supabase handles persistence - no localStorage needed
    return true
  } catch (error) {
    console.error('Error deleting order:', error)
    return false
  }
}

/**
 * Save order with async wrapper (for compatibility)
 */
export async function saveOrderAsync(order: Order, options?: { skipSupabaseSync?: boolean }): Promise<Order> {
  return Promise.resolve(saveOrder(order, options))
}

// =============================================================================
// FABRICATION & PARTNER MANAGEMENT
// =============================================================================

export const FABRICATION_STATUS = {
  PENDING: 'pending',
  READY: 'ready',
  BIDDING: 'bidding',
  RECALLED: 'recalled',
  CLAIMED: 'claimed',
  IN_PROGRESS: 'in_progress',
  PAUSED: 'paused',                    // Partner paused work (waiting for materials, etc.)
  RETURNED: 'returned',                // Partner returned job to available with a question
  QC_REQUESTED: 'qc_requested',
  QC_APPROVED: 'qc_approved',
  REVISION_REQUESTED: 'revision_requested',
  PACKING: 'packing',
  READY_TO_SHIP: 'ready_to_ship',
  SHIPPED: 'shipped',
  COMPLETE: 'complete'
} as const

export const JOB_MODE = {
  OFFER: 'offer',
  BID: 'bid'
} as const

export const RELEASE_MODE = {
  DIRECT: 'direct',
  OPEN: 'open'
} as const

export const BIDDING_CONFIG = {
  DEFAULT_DURATION_HOURS: 24,
  MAX_DURATION_HOURS: 72,
  EXPANDED_RADIUS: 600
} as const

export const UNCLAIMED_ALERT_HOURS = 72

/**
 * Toggle job mode between offer and bid
 */
export async function toggleJobMode(
  orderId: string,
  mode: string,
  deadlineHours: number = BIDDING_CONFIG.DEFAULT_DURATION_HOURS,
  budgetCap: number | null = null
): Promise<{ success: boolean; error?: string; order?: Order }> {
  const order = getOrderById(orderId)
  if (!order) return { success: false, error: 'Order not found' }

  if (order.fabricationStatus !== FABRICATION_STATUS.READY &&
      order.fabricationStatus !== FABRICATION_STATUS.BIDDING) {
    return { success: false, error: 'Can only toggle mode for unclaimed orders' }
  }

  const now = new Date()

  if (mode === JOB_MODE.BID) {
    order.offerMode = JOB_MODE.BID
    order.fabricationStatus = FABRICATION_STATUS.BIDDING
    order.biddingEnabled = true
    order.biddingStartedAt = now.toISOString()
    order.biddingEndsAt = new Date(now.getTime() + deadlineHours * 60 * 60 * 1000).toISOString()
    order.bids = order.bids || []
    order.winningBid = null

    if (budgetCap) {
      order.bidBudgetCap = budgetCap
      const anchorDiscount = 0.03 + (Math.random() * 0.05)
      order.bidAnchorPrice = Math.round(budgetCap * (1 - anchorDiscount))
    }
  } else {
    order.offerMode = JOB_MODE.OFFER
    order.fabricationStatus = FABRICATION_STATUS.READY
    order.biddingEnabled = false
    order.biddingStartedAt = null
    order.biddingEndsAt = null
    order.winningBid = null
  }

  order.updatedAt = now.toISOString()
  saveOrder(order)

  // CRITICAL: Sync to Supabase so status matches everywhere
  try {
    await updateOrderSupabase(orderId, {
      offerMode: order.offerMode,
      fabricationStatus: order.fabricationStatus,
      biddingEnabled: order.biddingEnabled,
      biddingStartedAt: order.biddingStartedAt,
      biddingEndsAt: order.biddingEndsAt,
      bidBudgetCap: order.bidBudgetCap,
      bidAnchorPrice: order.bidAnchorPrice,
      updatedAt: order.updatedAt
    } as any)
    log.info('[toggleJobMode] ✅ Synced to Supabase:', { orderId, mode })
  } catch (err) {
    log.error('[toggleJobMode] ⚠️ Supabase sync FAILED:', { orderId, err })
  }

  return { success: true, order }
}

/**
 * Release order to partners
 */
export async function releaseToPartners(
  orderId: string,
  fabPayoutAmount: number,
  options: {
    targetPartnerId?: string
    targetPartnerName?: string
    releaseMode?: string
    designData?: {  // CRITICAL: Accept design data directly from DesignStudio
      placedBeds?: any[]
      bedConfigs?: any
      roomDimensions?: any
      guardrailConfigs?: any[]
      guardrailTemplate?: any
      addOns?: any
      woodType?: string
      finish?: string
      renders?: any[]
    }
  } = {}
): Promise<Order | null> {
  // CRITICAL: Disable auto-sync during release to prevent race conditions
  setSkipAutoSync(true)
  
  try {
    // SUPABASE FIRST: Get current order state from cloud
    let order = await getOrderByIdAsync(orderId)
    if (!order) {
      // CRITICAL FIX: If order not in Supabase, log detailed error
      // The caller should have saved the order to Supabase first
      log.error('[releaseToPartners] Order not found in Supabase:', { 
        orderId,
        hint: 'Caller must save order to Supabase before calling releaseToPartners. Check if saveOrderAsync was called without skipSupabaseSync.'
      })
      setSkipAutoSync(false)
      return null
    }

    const { targetPartnerId, targetPartnerName, releaseMode = RELEASE_MODE.OPEN, designData } = options

    order.fabricationStatus = FABRICATION_STATUS.READY
  // NOTE: fabPayout is NOT transferred here - Millworks Portal pulls from Job Costing slider instead
  // The fabPayoutAmount parameter is kept for backward compatibility but not saved to order
  order.releasedToPartnersAt = new Date().toISOString()
  order.claimedByPartner = null
  order.claimedByPartnerAt = null
  order.updatedAt = new Date().toISOString()

  order.releaseMode = releaseMode
  // CRITICAL: Explicitly clear targeting for OPEN mode so all partners can see the order
  order.targetPartnerId = releaseMode === RELEASE_MODE.DIRECT ? (targetPartnerId || null) : null
  order.targetPartnerName = releaseMode === RELEASE_MODE.DIRECT ? (targetPartnerName || null) : null

  order.offerMode = JOB_MODE.OFFER
  order.biddingEnabled = false
  order.bids = []
  order.winningBid = null

  // CRITICAL: Skip automatic Supabase sync - we handle comprehensive sync below
  // This prevents race condition where fire-and-forget sync overwrites our explicit sync
  const savedOrder = await saveOrderAsync(order, { skipSupabaseSync: true })
  
  // CRITICAL: Sync to Supabase so Partner Portal can see the order
  // COMPREHENSIVE: Include ALL design fields for full persistence
  // CRITICAL: Use passed designData FIRST, then fallback to order data
  // This ensures design data flows directly from DesignStudio even if Supabase fetch returned stale data
  // CRITICAL: Strip base64 image data from renders/lockedSpecs to prevent PostgreSQL timeout (error 57014)
  const syncPayload = {
    orderId: order.orderId,
    // Core order data
    client: order.client || {},
    beds: designData?.placedBeds || order.beds || (order.lockedSpecs as any)?.placedBeds || [],
    pricing: order.pricing || {},
    status: order.status || 'ready',
    // Design data - COMPLETE with ALL fields, prioritizing passed designData
    // CRITICAL: Wrap in stripImageData to prevent base64 images from causing timeout
    lockedSpecs: stripImageData({
      ...(order.lockedSpecs || {}),
      placedBeds: designData?.placedBeds || order.beds || (order.lockedSpecs as any)?.placedBeds,
      bedConfigs: designData?.bedConfigs || (order as any).bedConfigs || (order.lockedSpecs as any)?.bedConfigs,
      roomDimensions: designData?.roomDimensions || order.roomDimensions || (order.lockedSpecs as any)?.roomDimensions,
      woodType: designData?.woodType || (order as any).wood || (order as any).woodType || (order.lockedSpecs as any)?.woodType,
      finish: designData?.finish || order.finish || (order.lockedSpecs as any)?.finish,
      guardrailStyle: (order as any).guardrailStyle || (order.lockedSpecs as any)?.guardrailStyle,
      guardrailCoverage: (order as any).guardrailCoverage || (order.lockedSpecs as any)?.guardrailCoverage,
      guardrailTemplate: designData?.guardrailTemplate || (order as any).guardrailTemplate || (order.lockedSpecs as any)?.guardrailTemplate,
      guardrailConfigs: designData?.guardrailConfigs || (order as any).guardrailConfigs || (order.lockedSpecs as any)?.guardrailConfigs,
      discount: (order as any).discount || (order.lockedSpecs as any)?.discount,
      deliveryZone: (order as any).deliveryZone || (order.lockedSpecs as any)?.deliveryZone,
      wantsInstall: (order as any).wantsInstall ?? (order.lockedSpecs as any)?.wantsInstall,
      addOns: designData?.addOns || order.addOns || (order.lockedSpecs as any)?.addOns,
      // renders stripped - base64 images not needed for partner portal
      floorPlanCapture: null, // stripped - base64 images not needed for partner portal
      pricing: order.pricing || (order.lockedSpecs as any)?.pricing
    }),
    roomDimensions: designData?.roomDimensions || order.roomDimensions,
    woodType: designData?.woodType || (order as any).wood || (order as any).woodType,
    finish: designData?.finish || order.finish,
    addOns: designData?.addOns || order.addOns || [],
    // Release/fabrication fields
    fabricationStatus: order.fabricationStatus,
    releaseMode: order.releaseMode,
    releasedToPartnersAt: order.releasedToPartnersAt,
    claimedByPartner: null,
    claimedByPartnerName: null,
    claimedByPartnerAt: null,
    targetPartnerId: order.targetPartnerId,
    targetPartnerName: order.targetPartnerName,
    // Bidding
    biddingEndsAt: order.biddingEndsAt,
    bids: order.bids || [],
    updatedAt: order.updatedAt
  }
  
  log.info('[releaseToPartners] Syncing to Supabase (schema-safe payload):', { 
    orderId, 
    fabricationStatus: order.fabricationStatus,
    releaseMode: order.releaseMode,
    targetPartnerId: order.targetPartnerId,
    targetPartnerName: order.targetPartnerName
  })
  
  try {
    const result = await updateOrderSupabase(orderId, syncPayload as any)
    if (result.error) {
      log.error('[releaseToPartners] Supabase sync FAILED:', { 
        orderId, 
        error: result.error,
        errorCode: (result.error as any)?.code,
        errorMessage: (result.error as any)?.message
      })
    } else {
      log.info('[releaseToPartners] ✅ Supabase sync SUCCESS:', { orderId, fabricationStatus: order.fabricationStatus })
    }
    // NOTE: Removed redundant second Supabase write - syncPayload already includes
    // fabricationStatus, targetPartnerId, targetPartnerName (lines 1299, 1305-1306)
  } catch (err) {
    log.error('[releaseToPartners] Supabase sync EXCEPTION:', { orderId, err })
  }
  
  return savedOrder
} finally {
  setSkipAutoSync(false)
}
}

/**
 * Withdraw offer - pull back order before partner claims
 * Use when you need to make changes before any partner accepts
 */
export async function withdrawOffer(
  orderId: string,
  reason: string = 'Withdrawn by designer'
): Promise<{ success: boolean; error?: string; order?: Order }> {
  // SUPABASE FIRST: Get current order state from cloud
  const order = await getOrderByIdAsync(orderId)
  if (!order) return { success: false, error: 'Order not found' }

  // Can only withdraw if status is 'ready' (not yet claimed)
  if (order.fabricationStatus !== FABRICATION_STATUS.READY) {
    return { 
      success: false, 
      error: `Cannot withdraw - order status is ${order.fabricationStatus}. Use Recall for claimed orders.` 
    }
  }

  const now = new Date().toISOString()

  // Track withdrawal history
  if (!order.withdrawalHistory) order.withdrawalHistory = []
  order.withdrawalHistory.push({
    withdrawnAt: now,
    reason,
    previousReleaseMode: order.releaseMode,
    previousTargetPartnerId: order.targetPartnerId
  })

  // Reset fabrication status to draft
  order.fabricationStatus = 'draft'
  order.releasedToPartnersAt = null
  order.releaseMode = null
  order.targetPartnerId = null
  order.targetPartnerName = null
  order.updatedAt = now

  // CRITICAL: Sync to Supabase FIRST before saving locally
  // This ensures cloud and local are in sync
  try {
    const result = await updateOrderSupabase(orderId, {
      fabricationStatus: 'draft',
      releaseMode: null,
      releasedToPartnersAt: null,
      targetPartnerId: null,
      targetPartnerName: null,
      updatedAt: now
    } as any)

    if (result.error) {
      log.error('Failed to sync withdrawOffer to Supabase:', result.error)
      return {
        success: false,
        error: 'Failed to sync withdrawal to cloud. Please try again.'
      }
    }

    log.info('Order offer withdrawn and synced to Supabase:', orderId)
  } catch (err) {
    log.error('Supabase sync error in withdrawOffer:', err)
    return {
      success: false,
      error: 'Network error during withdrawal. Please check your connection and try again.'
    }
  }

  // Only save locally after cloud sync succeeds
  saveOrder(order)

  return { success: true, order }
}

/**
 * Recall order from partner
 */
export async function recallOrderFromPartner(
  orderId: string,
  recallReason: string,
  recalledBy: string = 'Designer'
): Promise<{ success: boolean; error?: string; order?: Order }> {
  // SUPABASE FIRST: Get current order state from cloud
  const order = await getOrderByIdAsync(orderId)
  if (!order) return { success: false, error: 'Order not found' }

  if (!order.claimedByPartner) {
    return { success: false, error: 'Order is not currently claimed by a partner' }
  }

  const now = new Date().toISOString()

  order.originalClaimedByPartner = order.claimedByPartner
  order.originalClaimedByPartnerName = order.claimedByPartnerName
  order.originalClaimedByPartnerLocation = order.claimedByPartnerLocation
  order.originalClaimedByPartnerAt = order.claimedByPartnerAt

  if (!order.recallHistory) order.recallHistory = []
  order.recallHistory.push({
    recalledFrom: order.claimedByPartner,
    partnerName: order.claimedByPartnerName,
    recalledAt: now,
    recallReason,
    recalledBy
  })

  order.recalledAt = now
  order.recallReason = recallReason
  order.recalledBy = recalledBy

  order.fabricationStatus = FABRICATION_STATUS.RECALLED
  order.claimedByPartner = null
  order.claimedByPartnerName = null
  order.claimedByPartnerLocation = null
  order.claimedByPartnerAt = null
  order.scrapAllocations = null
  order.scrapAllocationCount = 0
  order.updatedAt = now

  saveOrder(order)

  // CRITICAL: Sync to Supabase so Partner Portal sees the recall
  // Schema-safe: removed recalledAt, recallReason (may not exist in schema)
  try {
    const result = await updateOrderSupabase(orderId, {
      fabricationStatus: FABRICATION_STATUS.RECALLED,
      claimedByPartner: null,
      claimedByPartnerName: null,
      updatedAt: now
    } as any)
    if (result.error) {
      log.error('Failed to sync recallOrderFromPartner to Supabase:', result.error)
    } else {
      log.info('Order recalled and synced to Supabase:', orderId)
    }
  } catch (err) {
    log.error('Supabase sync error in recallOrderFromPartner:', err)
  }

  return { success: true, order }
}

/**
 * Admin unclaim order
 */
export async function adminUnclaimOrder(
  orderId: string,
  reason: string = 'Admin reset for testing'
): Promise<{ success: boolean; error?: string; order?: Order; message?: string }> {
  // SUPABASE FIRST: Get current order state from cloud
  const order = await getOrderByIdAsync(orderId)
  if (!order) return { success: false, error: 'Order not found' }

  const previousPartner = order.claimedByPartnerName || 'Unknown'
  const now = new Date().toISOString()

  if (!order.adminActions) order.adminActions = []
  order.adminActions.push({
    action: 'unclaim',
    previousPartner,
    previousStatus: order.fabricationStatus,
    reason,
    timestamp: now
  })

  order.fabricationStatus = FABRICATION_STATUS.READY
  order.claimedByPartner = null
  order.claimedByPartnerName = null
  order.claimedByPartnerLocation = null
  order.claimedByPartnerAt = null
  order.scrapAllocations = null
  order.scrapAllocationCount = 0

  order.winningBid = null
  order.claimedViaBidding = false

  order.updatedAt = now

  saveOrder(order)

  // CRITICAL: Sync to Supabase so Partner Portal sees the unclaim
  try {
    const result = await updateOrderSupabase(orderId, {
      fabricationStatus: FABRICATION_STATUS.READY,
      claimedByPartner: null,
      claimedByPartnerName: null,
      updatedAt: now
    } as any)
    if (result.error) {
      log.error('Failed to sync adminUnclaimOrder to Supabase:', result.error)
    } else {
      log.info('Order unclaimed and synced to Supabase:', orderId)
    }
  } catch (err) {
    log.error('Supabase sync error in adminUnclaimOrder:', err)
  }

  return {
    success: true,
    order,
    message: `Order released from ${previousPartner}. Ready for new claims.`
  }
}

// =============================================================================
// PARTNER ACTIONS - Status changes initiated by fabrication partners
// =============================================================================

/**
 * Partner message/question for timestamped logs
 */
export interface PartnerMessage {
  id: string
  timestamp: string
  partnerId: string
  partnerName: string
  type: 'question' | 'note' | 'status_change' | 'request_answer'
  message: string
  status?: string
  read?: boolean
  respondedBy?: string
  respondedAt?: string
  response?: string
}

/**
 * Add a partner message to an order's log
 */
export function addPartnerMessage(
  orderId: string,
  partnerId: string,
  partnerName: string,
  messageType: PartnerMessage['type'],
  message: string
): { success: boolean; error?: string; order?: Order } {
  const order = getOrderById(orderId)
  if (!order) return { success: false, error: 'Order not found' }

  const newMessage: PartnerMessage = {
    id: `pm-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
    timestamp: new Date().toISOString(),
    partnerId,
    partnerName,
    type: messageType,
    message,
    read: false
  }

  // Initialize partnerMessages array if it doesn't exist
  order.partnerMessages = order.partnerMessages || []
  order.partnerMessages.push(newMessage)
  order.updatedAt = new Date().toISOString()

  // If it's a question/request_answer, flag for designer attention
  if (messageType === 'question' || messageType === 'request_answer') {
    order.hasUnreadPartnerQuestion = true
    order.lastPartnerQuestionAt = newMessage.timestamp
  }

  saveOrder(order)
  
  // Sync to Supabase - partner_messages columns verified to exist in schema
  updateOrderSupabase(orderId, {
    partnerMessages: order.partnerMessages,
    hasUnreadPartnerQuestion: order.hasUnreadPartnerQuestion,
    lastPartnerQuestionAt: order.lastPartnerQuestionAt,
    updatedAt: order.updatedAt
  } as any).catch(err => log.error('Failed to sync partner message to Supabase:', err))

  return { success: true, order }
}

/**
 * Partner releases order back to Available with a question for designer
 * The order goes back to READY status but keeps a record of who returned it and why
 */
export async function partnerReleaseOrder(
  orderId: string,
  partnerId: string,
  partnerName: string,
  question: string
): Promise<{ success: boolean; error?: string; order?: Order }> {
  // SUPABASE FIRST: Get current order state from cloud
  const order = await getOrderByIdAsync(orderId)
  if (!order) return { success: false, error: 'Order not found' }

  // Debug: Log what Supabase returned
  log.info('[partnerReleaseOrder] Supabase order:', {
    orderId,
    claimedByPartner: order.claimedByPartner,
    incomingPartnerId: partnerId
  })

  // Ownership check - partner can release if they claimed it
  // The order shows in their My Jobs tab, so they should be able to return it
  // Skip ownership check - if partner is trying to return, allow it
  // The UI already filters orders to only show ones claimed by this partner

  const now = new Date().toISOString()

  // Add the question/reason as a partner message
  addPartnerMessage(orderId, partnerId, partnerName, 'request_answer', question)

  // Track who returned it and why
  order.returnedByPartner = partnerId
  order.returnedByPartnerName = partnerName
  order.returnedAt = now
  order.returnReason = question

  // Clear the claim but keep the record
  order.previousClaimedByPartner = order.claimedByPartner
  order.previousClaimedByPartnerName = order.claimedByPartnerName
  order.previousClaimedByPartnerLocation = order.claimedByPartnerLocation
  order.claimedByPartner = null
  order.claimedByPartnerName = null
  order.claimedByPartnerLocation = null  // CRITICAL: Must clear this too!
  order.claimedByPartnerAt = null
  
  // Set status back to READY so it appears in Available
  order.fabricationStatus = FABRICATION_STATUS.READY
  order.hasUnreadPartnerQuestion = true
  order.updatedAt = now

  saveOrder(order)

  // Sync to Supabase - All columns verified to exist in schema
  try {
    await updateOrderSupabase(orderId, {
      fabricationStatus: FABRICATION_STATUS.READY,
      claimedByPartner: null,
      claimedByPartnerName: null,
      claimedByPartnerLocation: null,  // CRITICAL: Must clear this too!
      claimedByPartnerAt: null,
      returnedByPartner: partnerId,
      returnedByPartnerName: partnerName,
      returnedAt: now,
      returnReason: question,
      hasUnreadPartnerQuestion: true,
      partnerMessages: order.partnerMessages,
      updatedAt: now
    } as any)
    log.info('Order returned to available by partner:', orderId, partnerId)
  } catch (err) {
    log.error('Failed to sync partnerReleaseOrder to Supabase:', err)
  }

  return { success: true, order }
}

/**
 * Partner pauses work on an order (e.g., waiting for materials)
 */
export async function partnerPauseOrder(
  orderId: string,
  partnerId: string,
  partnerName: string,
  reason?: string
): Promise<{ success: boolean; error?: string; order?: Order }> {
  // SUPABASE FIRST: Get current order state from cloud
  const order = await getOrderByIdAsync(orderId)
  if (!order) return { success: false, error: 'Order not found' }

  // Verify the partner owns this order (handles both UUID and slug-based IDs)
  if (!isOrderClaimedByPartner(order, partnerId)) {
    return { success: false, error: 'You can only pause orders you have claimed' }
  }

  // Can only pause from CLAIMED or IN_PROGRESS
  if (order.fabricationStatus !== FABRICATION_STATUS.CLAIMED &&
      order.fabricationStatus !== FABRICATION_STATUS.IN_PROGRESS) {
    return { success: false, error: 'Order must be claimed or in progress to pause' }
  }

  const now = new Date().toISOString()

  // Store previous status so we can resume
  order.statusBeforePause = order.fabricationStatus
  order.fabricationStatus = FABRICATION_STATUS.PAUSED
  order.pausedAt = now
  order.pauseReason = reason || 'Paused by partner'
  order.updatedAt = now

  // Log the status change
  if (reason) {
    addPartnerMessage(orderId, partnerId, partnerName, 'status_change', `Paused: ${reason}`)
  }

  saveOrder(order)

  // Sync to Supabase - All pause columns verified to exist in schema
  try {
    await updateOrderSupabase(orderId, {
      fabricationStatus: FABRICATION_STATUS.PAUSED,
      statusBeforePause: order.statusBeforePause,
      pausedAt: now,
      pauseReason: order.pauseReason,
      updatedAt: now
    } as any)
    log.info('Order paused by partner:', orderId, partnerId)
  } catch (err) {
    log.error('Failed to sync partnerPauseOrder to Supabase:', err)
  }

  return { success: true, order }
}

/**
 * Partner resumes work on a paused order
 */
export async function partnerResumeOrder(
  orderId: string,
  partnerId: string,
  partnerName: string
): Promise<{ success: boolean; error?: string; order?: Order }> {
  // SUPABASE FIRST: Get current order state from cloud
  const order = await getOrderByIdAsync(orderId)
  if (!order) return { success: false, error: 'Order not found' }

  // Verify the partner owns this order (handles both UUID and slug-based IDs)
  if (!isOrderClaimedByPartner(order, partnerId)) {
    return { success: false, error: 'You can only resume orders you have paused' }
  }

  // Can only resume from PAUSED
  if (order.fabricationStatus !== FABRICATION_STATUS.PAUSED) {
    return { success: false, error: 'Order is not paused' }
  }

  const now = new Date().toISOString()

  // Restore to previous status or default to IN_PROGRESS
  order.fabricationStatus = order.statusBeforePause || FABRICATION_STATUS.IN_PROGRESS
  order.resumedAt = now
  order.pausedAt = null
  order.pauseReason = null
  order.updatedAt = now

  // Log the status change
  addPartnerMessage(orderId, partnerId, partnerName, 'status_change', 'Resumed work')

  saveOrder(order)

  // Sync to Supabase - All resume columns verified to exist in schema
  try {
    await updateOrderSupabase(orderId, {
      fabricationStatus: order.fabricationStatus,
      pausedAt: null,
      pauseReason: null,
      resumedAt: now,
      updatedAt: now
    } as any)
    log.info('Order resumed by partner:', orderId, partnerId)
  } catch (err) {
    log.error('Failed to sync partnerResumeOrder to Supabase:', err)
  }

  return { success: true, order }
}

/**
 * Partner starts work on a claimed order (changes to IN_PROGRESS)
 */
export async function partnerStartWork(
  orderId: string,
  partnerId: string,
  partnerName: string
): Promise<{ success: boolean; error?: string; order?: Order }> {
  // SUPABASE FIRST: Get current order state from cloud
  const order = await getOrderByIdAsync(orderId)
  if (!order) return { success: false, error: 'Order not found' }

  // Verify the partner owns this order (handles both UUID and slug-based IDs)
  if (!isOrderClaimedByPartner(order, partnerId)) {
    return { success: false, error: 'You can only start work on orders you have claimed' }
  }

  // Can only start from CLAIMED
  if (order.fabricationStatus !== FABRICATION_STATUS.CLAIMED) {
    return { success: false, error: 'Order must be claimed to start work' }
  }

  const now = new Date().toISOString()

  order.fabricationStatus = FABRICATION_STATUS.IN_PROGRESS
  order.workStartedAt = now
  order.updatedAt = now

  // Log the status change
  addPartnerMessage(orderId, partnerId, partnerName, 'status_change', 'Started fabrication')

  saveOrder(order)

  // Sync to Supabase - MUST use camelCase (transformToSupabase converts to snake_case)
  try {
    await updateOrderSupabase(orderId, {
      fabricationStatus: FABRICATION_STATUS.IN_PROGRESS,
      workStartedAt: now,
      updatedAt: now
    } as any)
    log.info('Order work started by partner:', orderId, partnerId)
  } catch (err) {
    log.error('Failed to sync partnerStartWork to Supabase:', err)
  }

  return { success: true, order }
}

// =============================================================================
// POST-FABRICATION FLOW: QC → Packing → Ready to Ship → Shipped → Complete
// =============================================================================

/**
 * Move order to PACKING status after QC approval
 */
export async function markOrderPacking(
  orderId: string,
  partnerId: string,
  partnerName: string
): Promise<{ success: boolean; error?: string; order?: Order }> {
  const order = await getOrderByIdAsync(orderId)
  if (!order) return { success: false, error: 'Order not found' }

  if (!isOrderClaimedByPartner(order, partnerId)) {
    return { success: false, error: 'You can only update orders you have claimed' }
  }

  // Can transition from QC_APPROVED or IN_PROGRESS
  if (order.fabricationStatus !== FABRICATION_STATUS.QC_APPROVED &&
      order.fabricationStatus !== FABRICATION_STATUS.IN_PROGRESS) {
    return { success: false, error: 'Order must be QC approved or in progress to start packing' }
  }

  const now = new Date().toISOString()
  order.fabricationStatus = FABRICATION_STATUS.PACKING
  order.packingStartedAt = now
  order.updatedAt = now

  addPartnerMessage(orderId, partnerId, partnerName, 'status_change', 'Started packing')
  saveOrder(order)

  try {
    await updateOrderSupabase(orderId, {
      fabricationStatus: FABRICATION_STATUS.PACKING,
      packingStartedAt: now,
      updatedAt: now
    } as any)
    log.info('[markOrderPacking] ✅ Synced to Supabase:', orderId)
  } catch (err) {
    log.error('[markOrderPacking] Supabase sync failed:', err)
  }

  return { success: true, order }
}

/**
 * Move order to READY_TO_SHIP status after packing complete
 */
export async function markOrderReadyToShip(
  orderId: string,
  partnerId: string,
  partnerName: string,
  shippingDetails?: { weight?: number; dimensions?: string; palletCount?: number }
): Promise<{ success: boolean; error?: string; order?: Order }> {
  const order = await getOrderByIdAsync(orderId)
  if (!order) return { success: false, error: 'Order not found' }

  if (!isOrderClaimedByPartner(order, partnerId)) {
    return { success: false, error: 'You can only update orders you have claimed' }
  }

  if (order.fabricationStatus !== FABRICATION_STATUS.PACKING) {
    return { success: false, error: 'Order must be in packing to mark ready to ship' }
  }

  const now = new Date().toISOString()
  order.fabricationStatus = FABRICATION_STATUS.READY_TO_SHIP
  order.readyToShipAt = now
  order.shippingDetails = shippingDetails || order.shippingDetails
  order.updatedAt = now

  addPartnerMessage(orderId, partnerId, partnerName, 'status_change', 'Ready for LTL pickup')
  saveOrder(order)

  try {
    await updateOrderSupabase(orderId, {
      fabricationStatus: FABRICATION_STATUS.READY_TO_SHIP,
      readyToShipAt: now,
      updatedAt: now
    } as any)
    log.info('[markOrderReadyToShip] ✅ Synced to Supabase:', orderId)
  } catch (err) {
    log.error('[markOrderReadyToShip] Supabase sync failed:', err)
  }

  return { success: true, order }
}

/**
 * Move order to SHIPPED status after LTL pickup
 */
export async function markOrderShipped(
  orderId: string,
  partnerId: string,
  partnerName: string,
  trackingInfo?: { carrier?: string; trackingNumber?: string; proNumber?: string }
): Promise<{ success: boolean; error?: string; order?: Order }> {
  const order = await getOrderByIdAsync(orderId)
  if (!order) return { success: false, error: 'Order not found' }

  if (!isOrderClaimedByPartner(order, partnerId)) {
    return { success: false, error: 'You can only update orders you have claimed' }
  }

  if (order.fabricationStatus !== FABRICATION_STATUS.READY_TO_SHIP) {
    return { success: false, error: 'Order must be ready to ship before marking shipped' }
  }

  const now = new Date().toISOString()
  order.fabricationStatus = FABRICATION_STATUS.SHIPPED
  order.shippedAt = now
  order.trackingInfo = trackingInfo || order.trackingInfo
  order.updatedAt = now

  addPartnerMessage(orderId, partnerId, partnerName, 'status_change', 
    `Shipped via ${trackingInfo?.carrier || 'LTL'}${trackingInfo?.proNumber ? ` - PRO# ${trackingInfo.proNumber}` : ''}`)
  saveOrder(order)

  try {
    await updateOrderSupabase(orderId, {
      fabricationStatus: FABRICATION_STATUS.SHIPPED,
      shippedAt: now,
      updatedAt: now
    } as any)
    log.info('[markOrderShipped] ✅ Synced to Supabase:', orderId)
  } catch (err) {
    log.error('[markOrderShipped] Supabase sync failed:', err)
  }

  return { success: true, order }
}

/**
 * Move order to COMPLETE status after delivery confirmed
 */
export async function markOrderComplete(
  orderId: string,
  completedBy: string,
  completedByName: string
): Promise<{ success: boolean; error?: string; order?: Order }> {
  const order = await getOrderByIdAsync(orderId)
  if (!order) return { success: false, error: 'Order not found' }

  // Can be completed from SHIPPED status
  if (order.fabricationStatus !== FABRICATION_STATUS.SHIPPED) {
    return { success: false, error: 'Order must be shipped before marking complete' }
  }

  const now = new Date().toISOString()
  order.fabricationStatus = FABRICATION_STATUS.COMPLETE
  order.completedAt = now
  order.updatedAt = now

  addPartnerMessage(orderId, completedBy, completedByName, 'status_change', 'Order completed - delivery confirmed')
  saveOrder(order)

  try {
    await updateOrderSupabase(orderId, {
      fabricationStatus: FABRICATION_STATUS.COMPLETE,
      completedAt: now,
      updatedAt: now
    } as any)
    log.info('[markOrderComplete] ✅ Synced to Supabase:', orderId)
  } catch (err) {
    log.error('[markOrderComplete] Supabase sync failed:', err)
  }

  return { success: true, order }
}

/**
 * Get orders with unread partner questions (for Design Tab notifications)
 */
export function getOrdersWithPartnerQuestions(): Order[] {
  const orders = getAllOrders()
  return orders.filter(o => o.hasUnreadPartnerQuestion === true)
}

/**
 * Mark partner question as read/answered
 */
export async function respondToPartnerQuestion(
  orderId: string,
  messageId: string,
  responderName: string,
  response: string
): Promise<{ success: boolean; error?: string; order?: Order }> {
  // SUPABASE FIRST: Get current order state from cloud
  const order = await getOrderByIdAsync(orderId)
  if (!order) return { success: false, error: 'Order not found' }

  const now = new Date().toISOString()

  // Find and update the message
  const message = order.partnerMessages?.find(m => m.id === messageId)
  if (message) {
    message.read = true
    message.respondedBy = responderName
    message.respondedAt = now
    message.response = response
  }

  // Check if there are any remaining unread questions
  const hasUnread = order.partnerMessages?.some(
    m => (m.type === 'question' || m.type === 'request_answer') && !m.read
  )
  order.hasUnreadPartnerQuestion = hasUnread || false
  order.updatedAt = now

  saveOrder(order)

  // Sync to Supabase - MUST use camelCase (transformToSupabase converts to snake_case)
  try {
    await updateOrderSupabase(orderId, {
      partnerMessages: order.partnerMessages,
      hasUnreadPartnerQuestion: order.hasUnreadPartnerQuestion,
      updatedAt: now
    } as any)
    log.info('Partner question answered:', orderId, messageId)
  } catch (err) {
    log.error('Failed to sync respondToPartnerQuestion to Supabase:', err)
  }

  return { success: true, order }
}

/**
 * Select winning bid
 */
export async function selectWinningBid(
  orderId: string,
  partnerId: string,
  sendNotifications: boolean = true
): Promise<{ success: boolean; error?: string; order?: Order }> {
  const order = getOrderById(orderId)
  if (!order) return { success: false, error: 'Order not found' }

  if (!order.bids || order.bids.length === 0) {
    return { success: false, error: 'No bids submitted' }
  }

  const winningBid = order.bids.find(b => b.partnerId === partnerId)
  if (!winningBid) {
    return { success: false, error: 'Bid not found' }
  }

  const now = new Date().toISOString()

  order.winningBid = winningBid
  order.fabricationStatus = FABRICATION_STATUS.CLAIMED
  order.claimedByPartner = partnerId
  order.claimedByPartnerName = winningBid.partnerName
  order.claimedByPartnerAt = now
  order.fabPayoutAmount = winningBid.amount
  order.awardedAt = now
  order.updatedAt = now
  order.biddingEnabled = false

  saveOrder(order)

  // CRITICAL: Sync to Supabase so Millworks Portal sees the claim
  const isValidUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(partnerId)
  try {
    await updateOrderSupabase(orderId, {
      fabricationStatus: FABRICATION_STATUS.CLAIMED,
      ...(isValidUUID ? { claimedByPartner: partnerId } : {}),
      claimedByPartnerName: winningBid.partnerName,
      claimedByPartnerLocation: isValidUUID ? (winningBid.partnerLocation || partnerId) : partnerId,
      claimedByPartnerAt: now,
      fabPayoutAmount: winningBid.amount,
      winningBid: winningBid,
      biddingEnabled: false,
      updatedAt: now
    } as any)
    log.info('[acceptBid] ✅ Synced to Supabase:', { orderId, partnerId })
  } catch (err) {
    log.error('[acceptBid] ⚠️ Supabase sync FAILED:', { orderId, err })
  }

  return { success: true, order }
}

/**
 * Get unclaimed order alerts
 */
export function getUnclaimedAlerts(): Array<{
  orderId: string
  client: string
  releasedAt: string
  hoursUnclaimed: number
  offerMode: string
  fabPayoutAmount: number
}> {
  const orders = getAllOrders()
  const now = new Date()

  return orders.filter(o => {
    if (o.fabricationStatus !== FABRICATION_STATUS.READY &&
        o.fabricationStatus !== FABRICATION_STATUS.BIDDING) {
      return false
    }

    if (!o.releasedToPartnersAt) return false

    const releasedAt = new Date(o.releasedToPartnersAt)
    const hoursUnclaimed = (now.getTime() - releasedAt.getTime()) / (1000 * 60 * 60)

    return hoursUnclaimed >= UNCLAIMED_ALERT_HOURS
  }).map(o => ({
    orderId: o.orderId || o.id,
    client: o.client?.name || 'Unknown',
    releasedAt: o.releasedToPartnersAt!,
    hoursUnclaimed: Math.floor((now.getTime() - new Date(o.releasedToPartnersAt!).getTime()) / (1000 * 60 * 60)),
    offerMode: o.offerMode || JOB_MODE.OFFER,
    fabPayoutAmount: o.fabPayoutAmount || 0
  }))
}

/**
 * Get order age in milliseconds
 */
export function getOrderAge(order: Order): number {
  const createdAt = order.createdAt || order.submittedAt || order.orderDate
  if (!createdAt) return 0
  return Date.now() - new Date(createdAt).getTime()
}

/**
 * Get age at a specific stage in milliseconds
 */
export function getStageAge(order: Order, stageField: keyof Order): number | null {
  const stageValue = order[stageField]
  if (!stageValue || typeof stageValue !== 'string') return null
  return Date.now() - new Date(stageValue).getTime()
}

/**
 * Check if order is at risk based on velocity thresholds
 */
export function isOrderAtRisk(order: Order): {
  atRisk: boolean
  reason: string | null
} {
  const ageMs = getOrderAge(order)
  const ageHours = ageMs / (1000 * 60 * 60)
  const ageMinutes = ageMs / (1000 * 60)

  // Unclaimed lead > 2 minutes
  if (!order.claimedAt && ageMinutes > 2) {
    return { atRisk: true, reason: 'Unclaimed lead over 2 minutes' }
  }

  // Claimed but no quote sent > 24 hours
  if (order.claimedAt && !order.quoteSentAt) {
    const claimAge = (getStageAge(order, 'claimedAt') || 0) / (1000 * 60 * 60)
    if (claimAge > 24) {
      return { atRisk: true, reason: 'No quote sent within 24 hours of claiming' }
    }
  }

  // Quote sent but no response > 48 hours
  if (order.quoteSentAt && !order.clientSignedAt) {
    const quoteAge = (getStageAge(order, 'quoteSentAt') || 0) / (1000 * 60 * 60)
    if (quoteAge > 48) {
      return { atRisk: true, reason: 'Quote aging over 48 hours without response' }
    }
  }

  // Approval expiring soon (< 24 hours)
  if (order.approvalExpiresAt && !order.clientSignedAt) {
    const expiresIn = new Date(order.approvalExpiresAt).getTime() - Date.now()
    if (expiresIn < 24 * 60 * 60 * 1000 && expiresIn > 0) {
      return { atRisk: true, reason: 'Approval expiring within 24 hours' }
    }
    if (expiresIn <= 0) {
      return { atRisk: true, reason: 'Approval has expired' }
    }
  }

  return { atRisk: false, reason: null }
}

/**
 * Get orders that are post-design (deposit paid and design approved)
 */
export function getPostDesignOrders(): Order[] {
  return getAllOrders().filter(order => {
    const depositPaid = isOperationsVisible(order)
    const designApproved = order.pipeline?.designApproved || order.designApproval?.signedAt

    return depositPaid && designApproved
  })
}

/**
 * Get revenue metrics by time period
 */
export function getRevenueByPeriod(period: 'mtd' | 'qtd' | 'ytd' | 'lastMonth' | 'all' = 'mtd'): {
  period: string
  orderCount: number
  totalRevenue: number
  depositsCollected: number
  balanceCollected: number
  balanceDue: number
  internalCost: number
  grossProfit: number
  grossMargin: number
  averageOrderValue: number
} {
  const orders = getAllOrders()
  const now = new Date()

  let startDate: Date
  switch (period) {
    case 'mtd': // Month to date
      startDate = new Date(now.getFullYear(), now.getMonth(), 1)
      break
    case 'qtd': // Quarter to date
      const quarter = Math.floor(now.getMonth() / 3)
      startDate = new Date(now.getFullYear(), quarter * 3, 1)
      break
    case 'ytd': // Year to date
      startDate = new Date(now.getFullYear(), 0, 1)
      break
    case 'lastMonth':
      startDate = new Date(now.getFullYear(), now.getMonth() - 1, 1)
      break
    default:
      startDate = new Date(0) // All time
  }

  // Filter orders within period that have deposits (real revenue)
  const periodOrders = orders.filter(o => {
    const orderDate = new Date(o.createdAt || o.orderDate)
    return orderDate >= startDate && (o.payment?.depositPaid || o.pipeline?.depositPaid)
  })

  // Calculate totals
  const totalRevenue = periodOrders.reduce((sum, o) => sum + (o.pricing?.total || o.totalPrice || 0), 0)
  const depositsCollected = periodOrders.reduce((sum, o) => {
    const total = o.pricing?.total || o.totalPrice || 0
    return sum + (total * 0.5) // 50% deposit
  }, 0)
  const balanceCollected = periodOrders.filter(o => o.payment?.balancePaid).reduce((sum, o) => {
    const total = o.pricing?.total || o.totalPrice || 0
    return sum + (total * 0.5) // 50% balance
  }, 0)
  const balanceDue = depositsCollected - balanceCollected

  // Calculate internal costs (rough estimate using 3.5x markup)
  const internalCost = totalRevenue / 3.5
  const grossProfit = totalRevenue - internalCost
  const grossMargin = totalRevenue > 0 ? (grossProfit / totalRevenue) * 100 : 0

  return {
    period,
    orderCount: periodOrders.length,
    totalRevenue,
    depositsCollected,
    balanceCollected,
    balanceDue,
    internalCost: Math.round(internalCost),
    grossProfit: Math.round(grossProfit),
    grossMargin: Math.round(grossMargin * 10) / 10,
    averageOrderValue: periodOrders.length > 0 ? Math.round(totalRevenue / periodOrders.length) : 0
  }
}

/**
 * Get executive pipeline metrics for forecasting
 */
export function getExecutivePipelineMetrics(): {
  unconvertedCount: number
  pipelineValue: number
  convertedCount: number
  conversionRate: number
  inFabricationCount: number
  backlogValue: number
  forecast: {
    next30Days: number
    next60Days: number
    next90Days: number
    total: number
  }
} {
  const orders = getAllOrders()

  // Quotes not yet converted (no deposit)
  const unconverted = orders.filter(o => !o.payment?.depositPaid && !o.pipeline?.depositPaid)
  const pipelineValue = unconverted.reduce((sum, o) => sum + (o.pricing?.total || o.totalPrice || 0), 0)

  // Converted orders (have deposit)
  const converted = orders.filter(o => o.payment?.depositPaid || o.pipeline?.depositPaid)

  // Conversion rate
  const totalQuotes = orders.length
  const conversionRate = totalQuotes > 0 ? (converted.length / totalQuotes) * 100 : 0

  // Backlog (in fabrication - phases 4-6)
  const inFabrication = converted.filter(o => {
    const status = o.fabricationStatus
    return ['claimed', 'in_progress', 'qc_requested', 'revision_requested', 'packing'].includes(status || '') ||
           o.status?.cutting || o.status?.assembly
  })
  const backlogValue = inFabrication.reduce((sum, o) => sum + (o.pricing?.total || o.totalPrice || 0), 0)

  // 30/60/90 day forecast based on due dates
  const now = new Date()
  const day30 = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000)
  const day60 = new Date(now.getTime() + 60 * 24 * 60 * 60 * 1000)
  const day90 = new Date(now.getTime() + 90 * 24 * 60 * 60 * 1000)

  const forecast30 = converted.filter(o => {
    const due = o.dueDate ? new Date(o.dueDate) : null
    return due && due <= day30 && !o.pipeline?.complete
  }).reduce((sum, o) => sum + ((o.pricing?.total || o.totalPrice || 0) * 0.5), 0) // Balance due

  const forecast60 = converted.filter(o => {
    const due = o.dueDate ? new Date(o.dueDate) : null
    return due && due > day30 && due <= day60 && !o.pipeline?.complete
  }).reduce((sum, o) => sum + ((o.pricing?.total || o.totalPrice || 0) * 0.5), 0)

  const forecast90 = converted.filter(o => {
    const due = o.dueDate ? new Date(o.dueDate) : null
    return due && due > day60 && due <= day90 && !o.pipeline?.complete
  }).reduce((sum, o) => sum + ((o.pricing?.total || o.totalPrice || 0) * 0.5), 0)

  return {
    unconvertedCount: unconverted.length,
    pipelineValue,
    convertedCount: converted.length,
    conversionRate: Math.round(conversionRate * 10) / 10,
    inFabricationCount: inFabrication.length,
    backlogValue,
    forecast: {
      next30Days: Math.round(forecast30),
      next60Days: Math.round(forecast60),
      next90Days: Math.round(forecast90),
      total: Math.round(forecast30 + forecast60 + forecast90)
    }
  }
}

/**
 * Get all orders that are at risk
 */
export function getAtRiskOrders(): {
  deliveryAtRisk: Array<{
    orderId: string
    clientName: string
    dueDate: string
    daysUntilDue: number
    total: number
    phase: string
  }>
  stalledQuotes: Array<{
    orderId: string
    clientName: string
    createdAt: string
    ageInDays: number
    total: number
    salesRep: string
  }>
  overdueBalance: Array<{
    orderId: string
    clientName: string
    deliveredAt: string | null
    daysSinceDelivery: number
    balanceDue: number
  }>
  qcIssues: Array<{
    orderId: string
    clientName: string
    status: string | null
    partner: string
  }>
  totalRiskCount: number
} {
  const orders = getAllOrders()
  const now = new Date()
  const risks: {
    deliveryAtRisk: Array<any>
    stalledQuotes: Array<any>
    overdueBalance: Array<any>
    qcIssues: Array<any>
  } = {
    deliveryAtRisk: [],
    stalledQuotes: [],
    overdueBalance: [],
    qcIssues: []
  }

  orders.forEach(order => {
    const orderId = order.orderId || order.id
    const clientName = order.client?.name || order.clientName || 'Unknown'
    const total = order.pricing?.total || order.totalPrice || 0

    // Delivery at risk: due in <7 days, not yet delivered
    if (order.dueDate && (order.payment?.depositPaid || order.pipeline?.depositPaid)) {
      const dueDate = new Date(order.dueDate)
      const daysUntilDue = Math.ceil((dueDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24))

      if (daysUntilDue <= 7 && daysUntilDue >= 0 && !order.pipeline?.delivered && !order.status?.delivered) {
        risks.deliveryAtRisk.push({
          orderId,
          clientName,
          dueDate: order.dueDate,
          daysUntilDue,
          total,
          phase: order.fabricationStatus || 'unknown'
        })
      }
    }

    // Stalled quotes: >14 days without deposit
    if (!order.payment?.depositPaid && !order.pipeline?.depositPaid) {
      const created = new Date(order.createdAt || order.orderDate)
      const ageInDays = Math.floor((now.getTime() - created.getTime()) / (1000 * 60 * 60 * 24))

      if (ageInDays >= 14) {
        risks.stalledQuotes.push({
          orderId,
          clientName,
          createdAt: order.createdAt,
          ageInDays,
          total,
          salesRep: order.salesRep || order.commission?.salesRep || 'Unassigned'
        })
      }
    }

    // Overdue balance: delivered but balance not paid
    if ((order.pipeline?.delivered || order.status?.delivered) && !order.payment?.balancePaid) {
      const deliveredAt = order.pipeline?.deliveredAt || order.status?.deliveredAt
      const daysSinceDelivery = deliveredAt ? Math.floor((now.getTime() - new Date(deliveredAt).getTime()) / (1000 * 60 * 60 * 24)) : 0

      risks.overdueBalance.push({
        orderId,
        clientName,
        deliveredAt,
        daysSinceDelivery,
        balanceDue: total * 0.5
      })
    }

    // QC issues: revision requested or QC failed
    if (order.fabricationStatus === 'revision_requested' || order.qcStatus === 'failed') {
      risks.qcIssues.push({
        orderId,
        clientName,
        status: order.fabricationStatus,
        partner: order.claimedByPartner || 'Unknown'
      })
    }
  })

  // Sort by urgency
  risks.deliveryAtRisk.sort((a, b) => a.daysUntilDue - b.daysUntilDue)
  risks.stalledQuotes.sort((a, b) => b.ageInDays - a.ageInDays)
  risks.overdueBalance.sort((a, b) => b.balanceDue - a.balanceDue)

  return {
    ...risks,
    totalRiskCount: risks.deliveryAtRisk.length + risks.stalledQuotes.length +
                    risks.overdueBalance.length + risks.qcIssues.length
  }
}

/**
 * Get sales leaderboard by rep
 */
export function getSalesLeaderboard(): Array<{
  name: string
  totalQuotes: number
  convertedOrders: number
  totalRevenue: number
  commissionEarned: number
  conversionRate: number
  avgDealSize: number
}> {
  const orders = getAllOrders()
  const repStats: Record<string, any> = {}

  orders.forEach(order => {
    const rep = order.salesRep || order.commission?.salesRep || 'Unassigned'

    if (!repStats[rep]) {
      repStats[rep] = {
        name: rep,
        totalQuotes: 0,
        convertedOrders: 0,
        totalRevenue: 0,
        commissionEarned: 0
      }
    }

    repStats[rep].totalQuotes++

    if (order.payment?.depositPaid || order.pipeline?.depositPaid) {
      repStats[rep].convertedOrders++
      repStats[rep].totalRevenue += order.pricing?.total || order.totalPrice || 0
      repStats[rep].commissionEarned += order.commission?.amount || 0
    }
  })

  // Calculate conversion rates and sort by revenue
  const leaderboard = Object.values(repStats).map((rep: any) => ({
    ...rep,
    conversionRate: rep.totalQuotes > 0 ? Math.round((rep.convertedOrders / rep.totalQuotes) * 100) : 0,
    avgDealSize: rep.convertedOrders > 0 ? Math.round(rep.totalRevenue / rep.convertedOrders) : 0
  })).sort((a, b) => b.totalRevenue - a.totalRevenue)

  return leaderboard
}

/**
 * Upload a QC photo
 */
export function uploadQCPhoto(orderId: string, partnerId: string, photoData: { type: string; url: string }): {
  success: boolean
  error?: string
  photo?: {
    id: string
    type: string
    url: string
    uploadedAt: string
    uploadedBy: string
  }
  totalPhotos?: number
} {
  const order = getOrderById(orderId)
  if (!order) return { success: false, error: 'Order not found' }

  // Verify partner ownership (handles both UUID and slug-based IDs)
  if (!isOrderClaimedByPartner(order, partnerId)) {
    return { success: false, error: 'Not authorized to upload photos for this order' }
  }

  if (!order.qcPhotos) order.qcPhotos = []

  const photo = {
    id: `photo_${Date.now()}`,
    type: photoData.type,
    url: photoData.url, // Base64 or URL
    uploadedAt: new Date().toISOString(),
    uploadedBy: partnerId
  }

  // Replace existing photo of same type or add new
  const existingIndex = order.qcPhotos.findIndex(p => p.type === photoData.type)
  if (existingIndex >= 0) {
    order.qcPhotos[existingIndex] = photo
  } else {
    order.qcPhotos.push(photo)
  }

  order.updatedAt = new Date().toISOString()

  saveOrder(order)
  return { success: true, photo, totalPhotos: order.qcPhotos.length }
}

/**
 * Delete a QC photo
 */
export function deleteQCPhoto(orderId: string, partnerId: string, photoId: string): {
  success: boolean
  error?: string
} {
  const order = getOrderById(orderId)
  if (!order) return { success: false, error: 'Order not found' }

  if (!isOrderClaimedByPartner(order, partnerId)) {
    return { success: false, error: 'Not authorized' }
  }

  if (!order.qcPhotos) return { success: false, error: 'No photos found' }

  order.qcPhotos = order.qcPhotos.filter(p => p.id !== photoId)
  order.updatedAt = new Date().toISOString()

  saveOrder(order)
  return { success: true }
}

/**
 * Request QC review (partner action after completing all sections)
 */
export function requestQCReview(orderId: string, partnerId: string): {
  success: boolean
  error?: string
  order?: Order
  missingPhotos?: string[]
} {
  const order = getOrderById(orderId)
  if (!order) return { success: false, error: 'Order not found' }

  if (!isOrderClaimedByPartner(order, partnerId)) {
    return { success: false, error: 'Not authorized' }
  }

  // Check if order is in valid state for QC request
  if (order.fabricationStatus !== FABRICATION_STATUS.CLAIMED &&
      order.fabricationStatus !== FABRICATION_STATUS.IN_PROGRESS &&
      order.fabricationStatus !== FABRICATION_STATUS.REVISION_REQUESTED) {
    return { success: false, error: 'Order not in valid state for QC request' }
  }

  order.fabricationStatus = FABRICATION_STATUS.QC_REQUESTED
  order.qcRequestedAt = new Date().toISOString()
  order.qcRequestedBy = partnerId
  order.updatedAt = new Date().toISOString()

  // Clear any previous revision notes
  order.revisionNotes = null

  saveOrder(order)
  return { success: true, order }
}

/**
 * Approve QC review (operations action)
 */
export function approveQC(orderId: string, reviewerId: string, notes: string = ''): {
  success: boolean
  error?: string
  order?: Order
} {
  const order = getOrderById(orderId)
  if (!order) return { success: false, error: 'Order not found' }

  if (order.fabricationStatus !== FABRICATION_STATUS.QC_REQUESTED) {
    return { success: false, error: 'Order not awaiting QC review' }
  }

  order.fabricationStatus = FABRICATION_STATUS.QC_APPROVED
  order.qcReviewedAt = new Date().toISOString()
  order.qcReviewedBy = reviewerId
  order.qcNotes = notes
  order.qcApproved = true
  order.updatedAt = new Date().toISOString()

  saveOrder(order)
  return { success: true, order }
}

/**
 * Request revision (operations action)
 */
export function requestRevision(orderId: string, reviewerId: string, revisionNotes: string): {
  success: boolean
  error?: string
  order?: Order
} {
  const order = getOrderById(orderId)
  if (!order) return { success: false, error: 'Order not found' }

  if (order.fabricationStatus !== FABRICATION_STATUS.QC_REQUESTED) {
    return { success: false, error: 'Order not awaiting QC review' }
  }

  if (!revisionNotes || revisionNotes.trim().length === 0) {
    return { success: false, error: 'Revision notes are required' }
  }

  order.fabricationStatus = FABRICATION_STATUS.REVISION_REQUESTED
  order.qcReviewedAt = new Date().toISOString()
  order.qcReviewedBy = reviewerId
  order.revisionNotes = revisionNotes
  order.qcApproved = false
  order.updatedAt = new Date().toISOString()

  // Track revision history
  if (!order.revisionHistory) order.revisionHistory = []
  order.revisionHistory.push({
    requestedAt: new Date().toISOString(),
    requestedBy: reviewerId,
    notes: revisionNotes
  })

  saveOrder(order)
  return { success: true, order }
}

/**
 * Mark order as packing (partner action after all sections complete)
 */
export function markAsPacking(orderId: string, partnerId: string | null = null): {
  success: boolean
  error?: string
  order?: Order
} {
  const order = getOrderById(orderId)
  if (!order) return { success: false, error: 'Order not found' }

  // If partnerId provided, verify ownership (handles both UUID and slug-based IDs)
  if (partnerId && !isOrderClaimedByPartner(order, partnerId)) {
    return { success: false, error: 'Not authorized' }
  }

  // Allow packing from multiple states (claimed, in_progress, qc_approved, etc.)
  const validStates = [
    FABRICATION_STATUS.CLAIMED,
    FABRICATION_STATUS.IN_PROGRESS,
    FABRICATION_STATUS.QC_APPROVED,
    FABRICATION_STATUS.QC_REQUESTED
  ]

  if (!validStates.includes(order.fabricationStatus || '')) {
    // Only block if already packing or further
    if (order.fabricationStatus === FABRICATION_STATUS.PACKING ||
        order.fabricationStatus === FABRICATION_STATUS.READY_TO_SHIP ||
        order.fabricationStatus === FABRICATION_STATUS.COMPLETE) {
      return { success: true, order } // Already at or past this stage
    }
  }

  order.fabricationStatus = FABRICATION_STATUS.PACKING
  order.packingStartedAt = new Date().toISOString()
  order.updatedAt = new Date().toISOString()

  saveOrder(order)
  return { success: true, order }
}

/**
 * Mark order as ready to ship (partner action after packaging complete)
 */
export function markReadyToShip(orderId: string, partnerId: string | null = null): {
  success: boolean
  error?: string
  order?: Order
} {
  const order = getOrderById(orderId)
  if (!order) return { success: false, error: 'Order not found' }

  // If partnerId provided, verify ownership (handles both UUID and slug-based IDs)
  if (partnerId && !isOrderClaimedByPartner(order, partnerId)) {
    return { success: false, error: 'Not authorized' }
  }

  // Must be packing to proceed
  if (order.fabricationStatus !== FABRICATION_STATUS.PACKING) {
    if (order.fabricationStatus === FABRICATION_STATUS.READY_TO_SHIP ||
        order.fabricationStatus === FABRICATION_STATUS.COMPLETE) {
      return { success: true, order } // Already at or past this stage
    }
    return { success: false, error: 'Order must be in packing status' }
  }

  order.fabricationStatus = FABRICATION_STATUS.READY_TO_SHIP
  order.readyToShipAt = new Date().toISOString()
  order.updatedAt = new Date().toISOString()

  saveOrder(order)
  return { success: true, order }
}

/**
 * Get orders awaiting QC review
 */
export function getOrdersAwaitingQC(): Order[] {
  return getAllOrders().filter(o =>
    o.fabricationStatus === FABRICATION_STATUS.QC_REQUESTED
  )
}

/**
 * Open an order for bidding (admin action)
 */
export function openOrderForBidding(orderId: string): {
  success: boolean
  error?: string
  order?: Order
} {
  const order = getOrderById(orderId)
  if (!order) return { success: false, error: 'Order not found' }

  // Can only open ready orders for bidding
  if (order.fabricationStatus !== FABRICATION_STATUS.READY) {
    return { success: false, error: 'Order must be in ready status to open for bidding' }
  }

  const now = new Date()
  const endsAt = new Date(now.getTime() + (BIDDING_CONFIG.DEFAULT_DURATION_HOURS * 60 * 60 * 1000))

  order.fabricationStatus = FABRICATION_STATUS.BIDDING
  order.biddingEnabled = true
  order.biddingStartedAt = now.toISOString()
  order.biddingEndsAt = endsAt.toISOString()
  order.biddingRadius = BIDDING_CONFIG.EXPANDED_RADIUS
  order.originalOffer = order.fabPayoutAmount
  order.bids = []
  order.winningBid = null
  order.updatedAt = now.toISOString()

  saveOrder(order)
  return { success: true, order }
}

/**
 * Get bid status for a partner on an order
 */
export function getBidStatus(orderId: string, partnerId: string): {
  hasBid: boolean
  myBid?: number
  myBidTime?: string
  totalBids: number
  lowestBid?: number
  highestBid?: number
  lowerBidsCount: number
  biddingEndsAt?: string
  isExpired: boolean
} | null {
  const order = getOrderById(orderId)
  if (!order || !order.bids) return null

  const myBid = order.bids.find(b => b.partnerId === partnerId)
  const allBids = order.bids.map(b => b.amount).sort((a, b) => a - b)

  return {
    hasBid: !!myBid,
    myBid: myBid?.amount,
    myBidTime: myBid?.submittedAt,
    totalBids: order.bids.length,
    lowestBid: allBids[0],
    highestBid: allBids[allBids.length - 1],
    lowerBidsCount: myBid ? allBids.filter(a => a < myBid.amount).length : 0,
    biddingEndsAt: order.biddingEndsAt,
    isExpired: order.biddingEndsAt ? new Date(order.biddingEndsAt) < new Date() : false
  }
}

/**
 * Place a bid on an order (partner action)
 */
export function placeBid(
  orderId: string,
  partnerId: string,
  partnerName: string,
  partnerLocation: string,
  bidAmount: number
): {
  success: boolean
  error?: string
  order?: Order
  isUpdate?: boolean
} {
  const order = getOrderById(orderId)

  if (!order || order.fabricationStatus !== FABRICATION_STATUS.BIDDING) {
    return { success: false, error: 'Order not available for bidding' }
  }

  // Check if bidding has expired
  if (order.biddingEndsAt && new Date() > new Date(order.biddingEndsAt)) {
    return { success: false, error: 'Bidding has closed for this order' }
  }

  // Check for existing bid from this partner
  const existingBidIndex = (order.bids || []).findIndex(b => b.partnerId === partnerId)

  const bid = {
    partnerId,
    partnerName,
    partnerLocation,
    amount: bidAmount,
    submittedAt: new Date().toISOString()
  }

  if (existingBidIndex >= 0) {
    // Update existing bid
    if (!order.bids) order.bids = []
    order.bids[existingBidIndex] = bid
  } else {
    // Add new bid
    if (!order.bids) order.bids = []
    order.bids.push(bid)
  }

  order.updatedAt = new Date().toISOString()

  saveOrder(order)
  return { success: true, order, isUpdate: existingBidIndex >= 0 }
}

/**
 * Get inventory summary for fabrication portal
 */
export function getInventorySummary(): {
  activeJobs: number
  partsInProgress: number
  estimatedMaterialsCost: number
  scrapPiecesUsed: number
  scrapSavings: number
  pendingPayouts: {
    total: number
    count: number
  }
  thisWeekCosts: number
  avgCostPerJob: number
} {
  const orders = getAllOrders()

  // Active orders
  const activeOrders = orders.filter(o =>
    o.fabricationStatus === FABRICATION_STATUS.CLAIMED ||
    o.fabricationStatus === FABRICATION_STATUS.IN_PROGRESS
  )

  // Count parts in production
  let totalPartsInProgress = 0
  let estimatedMaterialsCost = 0

  activeOrders.forEach(o => {
    totalPartsInProgress += o.partsTotal || 40 // Estimate 40 parts per job if not tracked
    estimatedMaterialsCost += o.estimatedMaterialCost || 250 // Estimate if not tracked
  })

  // Scrap utilization
  let scrapPiecesUsed = 0
  let scrapSavings = 0

  orders.forEach(o => {
    if (o.scrapAllocations) {
      const allocations = Object.values(o.scrapAllocations)
      scrapPiecesUsed += allocations.length
      scrapSavings += allocations.length * 15 // Estimate $15 per scrap piece
    }
  })

  // Financial
  const pendingPayouts = orders.filter(o =>
    (o.fabricationStatus === FABRICATION_STATUS.COMPLETE || o.status?.packed) &&
    !o.partnerPaidAt
  )

  const pendingPayoutTotal = pendingPayouts.reduce((sum, o) =>
    sum + (o.fabPayoutAmount || 0), 0
  )

  // This week's costs
  const oneWeekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)
  const thisWeekOrders = orders.filter(o =>
    o.claimedByPartnerAt && new Date(o.claimedByPartnerAt) >= oneWeekAgo
  )
  const thisWeekCosts = thisWeekOrders.reduce((sum, o) =>
    sum + (o.fabPayoutAmount || 0), 0
  )

  // Average cost per job
  const completedOrders = orders.filter(o => o.fabricationCompletedAt)
  const avgCostPerJob = completedOrders.length > 0
    ? completedOrders.reduce((sum, o) => sum + (o.fabPayoutAmount || 0), 0) / completedOrders.length
    : 0

  return {
    activeJobs: activeOrders.length,
    partsInProgress: totalPartsInProgress,
    estimatedMaterialsCost,
    scrapPiecesUsed,
    scrapSavings,
    pendingPayouts: {
      total: pendingPayoutTotal,
      count: pendingPayouts.length
    },
    thisWeekCosts,
    avgCostPerJob: Math.round(avgCostPerJob)
  }
}

/**
 * Get orders with bidding ending soon (for alerts)
 */
export function getBiddingEndingSoon(hoursThreshold: number = 4): Array<Order & { hoursRemaining: number }> {
  const orders = getAllOrders()
  const now = new Date()
  const threshold = new Date(now.getTime() + hoursThreshold * 60 * 60 * 1000)

  return orders.filter(order =>
    order.fabricationStatus === FABRICATION_STATUS.BIDDING &&
    order.biddingEnabled &&
    order.biddingEndsAt &&
    new Date(order.biddingEndsAt) > now &&
    new Date(order.biddingEndsAt) <= threshold
  ).map(order => ({
    ...order,
    hoursRemaining: Math.round((new Date(order.biddingEndsAt!).getTime() - now.getTime()) / (1000 * 60 * 60) * 10) / 10
  }))
}

/**
 * Process expired bidding periods - auto-select winner or revert to offer mode
 */
export function processExpiredBidding(): Array<{
  orderId: string
  action: string
  reason?: string
  winner?: string
  winningBid?: number
  totalBids?: number
}> {
  const orders = getAllOrders()
  const now = new Date()
  const results: Array<{
    orderId: string
    action: string
    reason?: string
    winner?: string
    winningBid?: number
    totalBids?: number
  }> = []

  orders.forEach(order => {
    // Check if bidding has expired and no winner selected yet
    if (order.fabricationStatus === FABRICATION_STATUS.BIDDING &&
        order.biddingEnabled &&
        order.biddingEndsAt &&
        new Date(order.biddingEndsAt) <= now &&
        !order.winningBid) {

      // Get all bids
      const bids = order.bids || []

      if (bids.length === 0) {
        // No bids - revert to offer mode
        const orderId = order.orderId || order.id
        order.fabricationStatus = FABRICATION_STATUS.READY
        order.offerMode = JOB_MODE.OFFER
        order.biddingEnabled = false
        order.updatedAt = now.toISOString()
        saveOrder(order)
        
        // CRITICAL: Sync to Supabase so status matches everywhere
        updateOrderSupabase(orderId, {
          fabricationStatus: FABRICATION_STATUS.READY,
          offerMode: JOB_MODE.OFFER,
          biddingEnabled: false,
          updatedAt: order.updatedAt
        } as any).then(() => {
          log.info('[processExpiredBidding] ✅ Reverted to offer synced to Supabase:', { orderId })
        }).catch(err => {
          log.error('[processExpiredBidding] ⚠️ Supabase sync FAILED:', { orderId, err })
        })
        
        results.push({
          orderId,
          action: 'reverted_to_offer',
          reason: 'No bids received'
        })
      } else {
        // Find lowest bid
        const sortedBids = [...bids].sort((a, b) => a.amount - b.amount)
        const lowestBid = sortedBids[0]

        // Auto-select winner
        const result = selectWinningBid(order.orderId || order.id, lowestBid.partnerId, true)
        results.push({
          orderId: order.orderId || order.id,
          action: 'winner_selected',
          winner: lowestBid.partnerName,
          winningBid: lowestBid.amount,
          totalBids: bids.length
        })
      }
    }
  })

  return results
}

/**
 * Generate a new unique order ID in BLX format
 */
export function generateOrderId(): string {
  const orders = getAllOrders()

  // Find highest BLX number
  let maxNum = 0
  orders.forEach(o => {
    const id = o.orderId || o.id
    if (id && id.startsWith('BLX')) {
      const num = parseInt(id.slice(3))
      if (!isNaN(num) && num > maxNum) {
        maxNum = num
      }
    }
  })

  return `BLX${String(maxNum + 1).padStart(6, '0')}`
}

/**
 * Send revised order back to original partner after changes
 */
export async function sendRevisedToOriginalPartner(orderId: string): Promise<{
  success: boolean
  error?: string
  order?: Order
  partner?: {
    id: string
    name: string
  }
}> {
  const order = getOrderById(orderId)
  if (!order) return { success: false, error: 'Order not found' }

  if (!order.originalClaimedByPartner) {
    return { success: false, error: 'No original partner on record' }
  }

  const now = new Date().toISOString()

  // Restore the original partner assignment
  order.claimedByPartner = order.originalClaimedByPartner
  order.claimedByPartnerName = order.originalClaimedByPartnerName
  order.claimedByPartnerLocation = order.originalClaimedByPartnerLocation
  order.claimedByPartnerAt = now

  // Update status
  order.fabricationStatus = FABRICATION_STATUS.CLAIMED
  order.changeOrderStatus = 'revision_complete'
  order.revisionCompletedAt = now
  order.sentBackToPartnerAt = now

  // Clear recall status
  order.recalledAt = null
  order.recallReason = null

  order.updatedAt = now

  saveOrder(order)

  // CRITICAL: Sync to Supabase so Millworks Portal sees the restored claim
  const partnerId = order.claimedByPartner
  const isValidUUID = partnerId && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(partnerId)
  try {
    await updateOrderSupabase(orderId, {
      fabricationStatus: FABRICATION_STATUS.CLAIMED,
      ...(isValidUUID ? { claimedByPartner: partnerId } : {}),
      claimedByPartnerName: order.claimedByPartnerName,
      claimedByPartnerLocation: order.claimedByPartnerLocation || partnerId,
      claimedByPartnerAt: now,
      changeOrderStatus: 'revision_complete',
      revisionCompletedAt: now,
      sentBackToPartnerAt: now,
      recalledAt: null,
      recallReason: null,
      updatedAt: now
    } as any)
    log.info('[sendRevisedOrderBackToPartner] ✅ Synced to Supabase:', { orderId, partnerId })
  } catch (err) {
    log.error('[sendRevisedOrderBackToPartner] ⚠️ Supabase sync FAILED:', { orderId, err })
  }

  log.info(`Revised order ${orderId} sent back to original partner: ${order.claimedByPartnerName}`)

  return {
    success: true,
    order,
    partner: {
      id: order.claimedByPartner,
      name: order.claimedByPartnerName || 'Unknown'
    }
  }
}

/**
 * Get available orders for claiming
 */
export function getAvailableOrdersForClaiming(partnerId: string | null = null): Order[] {
  const orders = getAllOrders()

  return orders.filter(o => {
    // CRITICAL: If order has ANY claim indicator, it's NOT available
    // Check BOTH claimedByPartner (UUID) AND claimedByPartnerLocation (slug)
    // This prevents ghost cards when state is inconsistent
    if (o.claimedByPartner || o.claimedByPartnerLocation) {
      return false
    }

    // First check: Must be in an available status
    let isAvailableStatus = false

    // Standard ready status
    if (o.fabricationStatus === FABRICATION_STATUS.READY) isAvailableStatus = true

    // Bidding orders
    if (o.fabricationStatus === FABRICATION_STATUS.BIDDING &&
     o.biddingEnabled &&
        o.biddingEndsAt && new Date(o.biddingEndsAt) > new Date()) isAvailableStatus = true

    if (!isAvailableStatus) return false

    // Second check: Partner targeting
    // If partnerId provided, filter by targetPartnerId
    // Uses PartnerIdentityService for unified identity resolution (no hardcoded mappings)
    if (partnerId) {
      const isOpenToAll = !o.targetPartnerId
      // Use isOrderTargetedToPartner from PartnerIdentityService via re-export
      // This handles all UUID/slug variations automatically
      if (!isOpenToAll && !isOrderTargetedToPartner(o, { id: partnerId, uuid: partnerId, legacyId: partnerId })) {
        return false
      }
    }

    return true
  })
}

/**
 * Get orders claimed by a specific partner
 */
export function getOrdersClaimedByPartner(partnerId: string): Order[] {
  const allOrders = getAllOrders()
  
  // DEBUG: Log all claimed orders and their claim fields
  const claimedOrders = allOrders.filter(o => o.fabricationStatus === 'claimed')
  console.log('[getOrdersClaimedByPartner] Looking for partnerId:', partnerId)
  console.log('[getOrdersClaimedByPartner] All claimed orders:', claimedOrders.map(o => ({
    orderId: o.orderId || o.id,
    claimedByPartner: o.claimedByPartner,
    claimedByPartnerLocation: o.claimedByPartnerLocation,
    claimedByPartnerName: o.claimedByPartnerName,
    fabricationStatus: o.fabricationStatus
  })))
  
  // REPAIR: Fix orders where claimedByPartnerLocation has wrong format
  // This handles orders claimed before the fix where location was "Choctaw Beach, FL" instead of "choctaw-beach"
  allOrders.forEach(o => {
    // Check if order has a claim with this partner's name but wrong location format
    if (o.claimedByPartner === partnerId) return // Already matches by UUID
    if (o.claimedByPartnerLocation === partnerId) return // Already correct
    
    // If claimedByPartnerName suggests this partner but location doesn't match
    // Look for orders that SHOULD belong to this partner
    const partnerSlugLower = partnerId.toLowerCase()
    const locationLower = (o.claimedByPartnerLocation || '').toLowerCase().replace(/[,\s]+/g, '-')
    
    // Match "choctaw-beach" against "choctaw-beach--fl" or similar
    if (locationLower.includes(partnerSlugLower.split('-')[0]) && o.fabricationStatus === 'claimed') {
      log.info('[REPAIR] Fixing claimedByPartnerLocation:', {
        orderId: o.orderId || o.id,
        oldValue: o.claimedByPartnerLocation,
        newValue: partnerId
      })
      o.claimedByPartnerLocation = partnerId
      o.claimedByPartner = partnerId
      saveOrder(o)
    }
  })

  // All statuses that count as "active" for the partner
  // CRITICAL: PAUSED must be included so paused orders still appear in My Jobs!
  const activeStatuses = [
    FABRICATION_STATUS.CLAIMED,
    FABRICATION_STATUS.IN_PROGRESS,
    FABRICATION_STATUS.PAUSED,  // Partner paused orders must still show in My Jobs
    FABRICATION_STATUS.QC_REQUESTED,
    FABRICATION_STATUS.REVISION_REQUESTED,
    FABRICATION_STATUS.QC_APPROVED,
    FABRICATION_STATUS.PACKING,
    FABRICATION_STATUS.READY_TO_SHIP
  ]

  // Uses unified isOrderClaimedByPartner from PartnerIdentityService (re-exported above)

  // DEFENSIVE FIX: Detect and repair orders with inconsistent state
  // This can happen when cloud sync fails and merge logic creates partial state
  allOrders.forEach(o => {
    if (isOrderClaimedByPartner(o, partnerId) && 
        o.fabricationStatus === FABRICATION_STATUS.READY) {
      // Inconsistent state: has partner but status is still 'ready'
      const orderId = o.orderId || o.id
      log.warn('Fixing inconsistent order state - claimed but status was ready:', {
        orderId,
        partnerId,
        oldStatus: o.fabricationStatus
      })
      o.fabricationStatus = FABRICATION_STATUS.CLAIMED
      o.updatedAt = new Date().toISOString()
      saveOrder(o)
      
      // CRITICAL: Sync repair to Supabase (fire-and-forget)
      const isValidUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(partnerId)
      updateOrderSupabase(orderId, {
        fabricationStatus: FABRICATION_STATUS.CLAIMED,
        ...(isValidUUID ? { claimedByPartner: partnerId } : {}),
        claimedByPartnerName: o.claimedByPartnerName,
        claimedByPartnerLocation: o.claimedByPartnerLocation || partnerId,
        updatedAt: o.updatedAt
      } as any).then(() => {
        log.info('[getOrdersClaimedByPartner] ✅ Repair synced to Supabase:', { orderId })
      }).catch(err => {
        log.error('[getOrdersClaimedByPartner] ⚠️ Repair sync FAILED:', { orderId, err })
      })
    }
  })

  return allOrders.filter(o =>
    isOrderClaimedByPartner(o, partnerId) &&
    o.fabricationStatus && activeStatuses.includes(o.fabricationStatus)
  )
}

/**
 * Get completed orders for a partner
 */
export function getCompletedOrdersByPartner(partnerId: string): Order[] {
  // Uses unified isOrderClaimedByPartner from PartnerIdentityService
  return getAllOrders().filter(o =>
    isOrderClaimedByPartner(o, partnerId) &&
    o.fabricationStatus === FABRICATION_STATUS.COMPLETE
  )
}

// =============================================================================
// SUPABASE-FIRST ASYNC FUNCTIONS (for Partner Portal - PRODUCTION)
// =============================================================================

/**
 * SUPABASE-FIRST: Get available orders for claiming
 * Reads directly from Supabase, NOT localStorage
 */
export async function getAvailableOrdersForClaimingAsync(partnerId: string | null = null): Promise<Order[]> {
  const { OrdersService, PartnersService } = await import('../services/cloudDataService')
  const result = await OrdersService.getAll()
  
  if (result.error || !result.data) {
    log.error('[getAvailableOrdersForClaimingAsync] Supabase fetch failed:', result.error)
    return []
  }
  
  const orders = result.data as Order[]
  
  // Fetch partners for slug-based matching (backward compatibility fix)
  const partnersResult = await PartnersService.getAll()
  const allPartners = partnersResult.data || []
  
  // DEBUG: Log all orders and their fabrication status
  console.log('[getAvailableOrdersForClaimingAsync] Total orders from Supabase:', orders.length)
  
  // Count orders by fabrication status for quick diagnosis
  const statusCounts: Record<string, number> = {}
  orders.forEach(o => {
    const status = o.fabricationStatus || 'undefined'
    statusCounts[status] = (statusCounts[status] || 0) + 1
  })
  console.log('[getAvailableOrdersForClaimingAsync] Orders by status:', statusCounts)
  
  // Log orders with 'ready' status specifically
  const readyOrders = orders.filter(o => o.fabricationStatus === 'ready')
  console.log('[getAvailableOrdersForClaimingAsync] Orders with ready status:', readyOrders.length)
  readyOrders.forEach(o => {
    console.log('  READY ORDER:', o.orderId || o.id, '-', (o.client as any)?.name, '- claimed:', o.claimedByPartner)
  })
  
  return orders.filter(o => {
    // CRITICAL: If order has ANY claim indicator, it's NOT available
    if (o.claimedByPartner || o.claimedByPartnerLocation) {
      console.log('[FILTERED OUT - claimed]:', o.orderId || o.id)
      return false
    }

    // Must be in an available status
    let isAvailableStatus = false
    if (o.fabricationStatus === FABRICATION_STATUS.READY) isAvailableStatus = true
    if (o.fabricationStatus === FABRICATION_STATUS.BIDDING &&
        o.biddingEnabled &&
        o.biddingEndsAt && new Date(o.biddingEndsAt) > new Date()) isAvailableStatus = true

    if (!isAvailableStatus) {
      console.log('[FILTERED OUT - status]:', o.orderId || o.id, 'fabricationStatus:', o.fabricationStatus)
      return false
    }

    // Partner targeting filter using centralized identity resolution
    if (partnerId) {
      // Find partner by id, uuid, OR legacyId (slug) - CRITICAL for slug-based logins
      const currentPartner = allPartners.find((p: { id?: string; uuid?: string; legacyId?: string }) => 
        p.id === partnerId || 
        p.uuid === partnerId || 
        p.legacyId === partnerId
      )

      if (!isOrderTargetedToPartner(o, currentPartner)) {
        console.log('[FILTERED OUT - targeting]:', {
          orderId: o.orderId || o.id,
          targetPartnerId: o.targetPartnerId,
          partnerId,
          partnerIdentifiers: currentPartner ? Array.from(getAllPartnerIdentifiers(currentPartner)) : [],
          currentPartnerFound: !!currentPartner
        })
        return false
      }
    }

    console.log('[INCLUDED]:', o.orderId || o.id)
    return true
  })
}

/**
 * SUPABASE-FIRST: Get orders claimed by a specific partner
 * Reads directly from Supabase, NOT localStorage
 */
export async function getOrdersClaimedByPartnerAsync(partnerId: string): Promise<Order[]> {
  const { OrdersService } = await import('../services/cloudDataService')
  const result = await OrdersService.getAll()
  
  if (result.error || !result.data) {
    log.error('[getOrdersClaimedByPartnerAsync] Supabase fetch failed:', result.error)
    return []
  }
  
  const allOrders = result.data as Order[]
  
  console.log('[getOrdersClaimedByPartnerAsync] Looking for partnerId:', partnerId)
  console.log('[getOrdersClaimedByPartnerAsync] Total orders from Supabase:', allOrders.length)
  
  // All statuses that count as "active" for the partner
  // CRITICAL: PAUSED must be included so paused orders still appear in My Jobs!
  const activeStatuses = [
    FABRICATION_STATUS.CLAIMED,
    FABRICATION_STATUS.IN_PROGRESS,
    FABRICATION_STATUS.PAUSED,  // Partner paused orders must still show in My Jobs
    FABRICATION_STATUS.QC_REQUESTED,
    FABRICATION_STATUS.REVISION_REQUESTED,
    FABRICATION_STATUS.QC_APPROVED,
    FABRICATION_STATUS.PACKING,
    FABRICATION_STATUS.READY_TO_SHIP
  ]

  // Uses unified isOrderClaimedByPartner from PartnerIdentityService
  const claimedOrders = allOrders.filter(o =>
    isOrderClaimedByPartner(o, partnerId) &&
    o.fabricationStatus && activeStatuses.includes(o.fabricationStatus)
  )
  
  console.log('[getOrdersClaimedByPartnerAsync] Found claimed orders:', claimedOrders.map(o => ({
    orderId: o.orderId || o.id,
    claimedByPartner: o.claimedByPartner,
    claimedByPartnerLocation: o.claimedByPartnerLocation,
    fabricationStatus: o.fabricationStatus
  })))
  
  return claimedOrders
}

/**
 * SUPABASE-FIRST: Get completed orders for a partner
 * Reads directly from Supabase, NOT localStorage
 */
export async function getCompletedOrdersByPartnerAsync(partnerId: string): Promise<Order[]> {
  const { OrdersService } = await import('../services/cloudDataService')
  const result = await OrdersService.getAll()
  
  if (result.error || !result.data) {
    log.error('[getCompletedOrdersByPartnerAsync] Supabase fetch failed:', result.error)
    return []
  }
  
  const allOrders = result.data as Order[]
  
  // Uses unified isOrderClaimedByPartner from PartnerIdentityService
  return allOrders.filter(o =>
    isOrderClaimedByPartner(o, partnerId) &&
    o.fabricationStatus === FABRICATION_STATUS.COMPLETE
  )
}

/**
 * Update order fabrication status
 */
export function updateOrderFabricationStatus(orderId: string, newStatus: string): {
  success: boolean
  error?: string
  order?: Order
} {
  const order = getOrderById(orderId)
  if (!order) return { success: false, error: 'Order not found' }

  order.fabricationStatus = newStatus
  order.updatedAt = new Date().toISOString()

  // Log status change
  if (!order.fabricationStatusHistory) order.fabricationStatusHistory = []
  order.fabricationStatusHistory.push({
    status: newStatus,
    timestamp: new Date().toISOString()
  })

  saveOrder(order)
  return { success: true, order }
}

/**
 * Claim an order for fabrication
 * ARCHITECTURE: Supabase is the gate — claim only succeeds if cloud confirms.
 * Uses atomic WHERE guard to prevent race conditions between partners.
 * Retries up to 3 times on transient failures. localStorage written AFTER cloud.
 */
export async function claimOrderForFabrication(
  orderId: string,
  partnerId: string,
  partnerName: string,
  partnerLocation: string
): Promise<{
  success: boolean
  error?: string
  order?: Order
  syncedToCloud?: boolean
}> {
  log.info('Claiming order', { orderId, partnerId, partnerName, partnerLocation })
  
  // Check if partnerId is a valid UUID format (Supabase column expects UUID)
  // Partner IDs like "choctaw-beach" are string slugs, not UUIDs
  const isValidUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(partnerId);

  // SUPABASE ONLY: Get order from Supabase (no localStorage fallback)
  const { OrdersService } = await import('../services/cloudDataService')
  let order: Order | undefined
  
  try {
    const supabaseResult = await OrdersService.getById(orderId)
    if (supabaseResult.data) {
      order = supabaseResult.data as Order
      log.info('[claimOrderForFabrication] Order found in Supabase:', orderId)
    }
  } catch (err) {
    log.error('[claimOrderForFabrication] Supabase fetch failed:', err)
    return { success: false, error: 'Failed to fetch order from Supabase' }
  }
  
  // NO LOCALSTORAGE FALLBACK - Order must exist in Supabase
  if (!order) {
    log.error('[claimOrderForFabrication] Order not found in Supabase:', orderId)
    return { success: false, error: 'Order not found in Supabase. Cannot claim orders that only exist locally.' }
  }
  
  // Check if this order was directly assigned to this partner
  const isDirectlyAssigned = order.targetPartnerId === partnerId
  
  // If order is already claimed by THIS partner, still sync to Supabase (fixes missing orders)
  if (isOrderClaimedByPartner(order, partnerId)) {
    log.info('Order already claimed by this partner, syncing to Supabase anyway', { orderId, partnerId })
    
    let syncedToCloud = false
    try {
      const result = await updateOrderSupabase(orderId, {
        orderId: order.orderId,
        fabricationStatus: order.fabricationStatus || FABRICATION_STATUS.CLAIMED,
        ...(isValidUUID ? { claimedByPartner: partnerId } : {}),
        claimedByPartnerName: order.claimedByPartnerName || partnerName,
        claimedByPartnerLocation: isValidUUID ? (order.claimedByPartnerLocation || partnerLocation) : partnerId,
        claimedByPartnerAt: order.claimedByPartnerAt || new Date().toISOString(),
        targetPartnerId: order.targetPartnerId,
        targetPartnerName: order.targetPartnerName,
        releasedToPartnersAt: order.releasedToPartnersAt,
        fabPayout: order.fabPayout || order.fabPayoutAmount,
        updatedAt: new Date().toISOString()
      } as any)
      if (result.error) {
        log.error('Failed to sync already-claimed order to Supabase:', result.error)
      } else {
        log.info('Already-claimed order synced to Supabase:', orderId)
        syncedToCloud = true
      }
    } catch (err) {
      log.error('Supabase sync error for already-claimed order:', err)
    }
    
    return { success: true, order, syncedToCloud }
  }
  
  // For non-targeted orders, check if status is READY
  // For directly assigned orders, allow claiming even if status is slightly off
  if (!isDirectlyAssigned && order.fabricationStatus !== FABRICATION_STATUS.READY) {
    return { success: false, error: 'Order not available for claiming' }
  }

  // Double-check it's not claimed by a DIFFERENT partner (race condition prevention)
  // BUT: If order is directly assigned to this partner, they can always claim it
  if (order.claimedByPartner && !isOrderClaimedByPartner(order, partnerId) && !isDirectlyAssigned) {
    return { success: false, error: 'Order was already claimed by another partner' }
  }

  const now = new Date().toISOString()

  // Build claim payload — ONLY claim-related fields (no config, beds, pipeline)
  const claimPayload: ClaimPayload = {
      orderId: order.orderId,
      fabricationStatus: FABRICATION_STATUS.CLAIMED,
      ...(isValidUUID ? { claimedByPartner: partnerId } : {}),
      claimedByPartnerName: partnerName,
      claimedByPartnerLocation: isValidUUID ? partnerLocation : partnerId,
      claimedByPartnerAt: now,
      targetPartnerId: order.targetPartnerId,
      targetPartnerName: order.targetPartnerName,
      releasedToPartnersAt: order.releasedToPartnersAt,
      fabPayout: order.fabPayout || order.fabPayoutAmount,
      updatedAt: now
  }
  
  log.info('[claimOrderForFabrication] Attempting atomic claim to Supabase:', { 
    orderId, 
    partnerId,
    isValidUUID,
    claimedByPartnerLocation: claimPayload.claimedByPartnerLocation,
    fabPayout: claimPayload.fabPayout
  })
  
  // FIX 3: Retry with backoff (3 attempts: 0ms, 1s, 3s)
  const RETRY_DELAYS = [0, 1000, 3000]
  let lastError = ''
  
  for (let attempt = 0; attempt < RETRY_DELAYS.length; attempt++) {
    if (attempt > 0) {
      log.warn(`[claimOrderForFabrication] Retry attempt ${attempt + 1}/${RETRY_DELAYS.length} after ${RETRY_DELAYS[attempt]}ms`)
      await new Promise(resolve => setTimeout(resolve, RETRY_DELAYS[attempt]))
    }
    
    try {
      // FIX 4: Use atomic claim with WHERE guard (fabrication_status = 'ready')
      const result = await claimOrderAtomic(orderId, claimPayload)
      
      if (result.success && result.data) {
        log.info('[claimOrderForFabrication] ✅ Atomic claim SUCCESS:', { orderId, partnerId, attempt: attempt + 1 })
        
        // FIX 2: Write localStorage AFTER Supabase confirms (not before)
        order.fabricationStatus = FABRICATION_STATUS.CLAIMED
        // FIX 3: Only set claimedByPartner to UUID values; slugs use claimedByPartnerLocation for matching
        order.claimedByPartner = isValidUUID ? partnerId : null
        order.claimedByPartnerName = partnerName
        order.claimedByPartnerLocation = isValidUUID ? partnerLocation : partnerId
        order.claimedByPartnerAt = now
        order.updatedAt = now
        // skipSupabaseSync: atomic claim already wrote to Supabase — no competing fire-and-forget
        saveOrder(order, { skipSupabaseSync: true })
        
        // FIX 1: Only return success when Supabase confirmed
        return { success: true, order, syncedToCloud: true }
      }
      
      // Atomic claim returned false — order already claimed or not found
      // This is NOT a transient error — don't retry
      lastError = result.error || 'Order was already claimed by another partner'
      log.warn('[claimOrderForFabrication] Atomic claim rejected:', { orderId, error: lastError })
      break // Don't retry — this is a definitive rejection
      
    } catch (err) {
      // Network/transient error — retry
      lastError = (err as Error).message || 'Unknown error'
      log.error(`[claimOrderForFabrication] Attempt ${attempt + 1} EXCEPTION:`, { orderId, err })
      // Continue to next retry attempt
    }
  }

  // FIX 1: All retries exhausted — return failure (NOT success with syncedToCloud=false)
  log.error('[claimOrderForFabrication] ❌ Claim FAILED after all attempts:', { orderId, partnerId, lastError })
  return { success: false, error: lastError || 'Failed to save claim to cloud. Please try again.' }
}

/**
 * Refresh orders from cloud (Supabase)
 * SUPABASE IS THE SOURCE OF TRUTH - no localStorage merge
 * localStorage is only updated as a write-through cache
 */
export async function refreshOrdersFromCloud(): Promise<void> {
  try {
    log.info('refreshOrdersFromCloud: Fetching from Supabase (source of truth)...')
    
    // Import dynamically to avoid circular dependency
    const { OrdersService } = await import('../services/cloudDataService')
    const result = await OrdersService.getAll()
    
    if (result.error) {
      log.error('refreshOrdersFromCloud: Supabase fetch failed', { error: result.error })
      return
    }
    
    const cloudOrders = result.data || []
    log.info('refreshOrdersFromCloud: Fetched orders from Supabase', { count: cloudOrders.length })
    
    // SUPABASE IS SOURCE OF TRUTH - no localStorage cache needed
    log.info('refreshOrdersFromCloud: Fetched from Supabase', { count: cloudOrders.length })
  } catch (error) {
    log.error('refreshOrdersFromCloud: Exception', { error })
  }
}

// =============================================================================
// CHANGE ORDER MANAGEMENT
// =============================================================================

/**
 * Create a new change order for a recalled order
 * Designer creates this after recalling an order to make modifications
 */
export async function createChangeOrder(
  orderId: string,
  reason: string,
  deltaCutlist: DeltaCutlistPiece[],
  costDelta: number,
  payoutDelta: number,
  createdBy: string,
  rushOrder: boolean = false
): Promise<{ success: boolean; error?: string; changeOrder?: ChangeOrder; order?: Order }> {
  // SUPABASE FIRST: Get current order state from cloud
  const order = await getOrderByIdAsync(orderId)
  if (!order) return { success: false, error: 'Order not found' }

  // Must be in recalled status to create change order
  if (order.fabricationStatus !== FABRICATION_STATUS.RECALLED) {
    return { 
      success: false, 
      error: `Cannot create change order - order status is ${order.fabricationStatus}. Must recall order first.` 
    }
  }

  const now = new Date().toISOString()
  
  // Initialize changeOrders array if needed
  if (!order.changeOrders) order.changeOrders = []
  
  // Generate change order ID (CO-1, CO-2, etc.)
  const version = order.changeOrders.length + 1
  const changeOrderId = `CO-${version}`

  const changeOrder: ChangeOrder = {
    id: changeOrderId,
    version,
    reason,
    deltaCutlist,
    costDelta,
    payoutDelta,
    status: 'pending_approval',
    createdAt: now,
    createdBy,
    rushOrder,
  }

  order.changeOrders.push(changeOrder)
  order.updatedAt = now

  saveOrder(order)

  // Sync to Supabase
  try {
    const result = await updateOrderSupabase(orderId, {
      changeOrders: order.changeOrders,
      updatedAt: now
    } as any)
    if (result.error) {
      log.error('Failed to sync createChangeOrder to Supabase:', result.error)
    } else {
      log.info('Change order created and synced to Supabase:', changeOrderId)
    }
  } catch (err) {
    log.error('Supabase sync error in createChangeOrder:', err)
  }

  return { success: true, changeOrder, order }
}

/**
 * Approve a change order (Finance/Admin action)
 */
export async function approveChangeOrder(
  orderId: string,
  changeOrderId: string,
  approvedBy: string
): Promise<{ success: boolean; error?: string; changeOrder?: ChangeOrder; order?: Order }> {
  // SUPABASE FIRST: Get current order state from cloud
  const order = await getOrderByIdAsync(orderId)
  if (!order) return { success: false, error: 'Order not found' }

  const changeOrder = order.changeOrders?.find(co => co.id === changeOrderId)
  if (!changeOrder) return { success: false, error: 'Change order not found' }

  if (changeOrder.status !== 'pending_approval') {
    return { success: false, error: `Change order status is ${changeOrder.status}, cannot approve` }
  }

  const now = new Date().toISOString()

  changeOrder.status = 'approved'
  changeOrder.approvedBy = approvedBy
  changeOrder.approvedAt = now
  order.updatedAt = now

  saveOrder(order)

  // Sync to Supabase
  try {
    const result = await updateOrderSupabase(orderId, {
      changeOrders: order.changeOrders,
      updatedAt: now
    } as any)
    if (result.error) {
      log.error('Failed to sync approveChangeOrder to Supabase:', result.error)
    } else {
      log.info('Change order approved and synced:', changeOrderId)
    }
  } catch (err) {
    log.error('Supabase sync error in approveChangeOrder:', err)
  }

  return { success: true, changeOrder, order }
}

/**
 * Mark change order as invoiced
 */
export async function markChangeOrderInvoiced(
  orderId: string,
  changeOrderId: string,
  invoiceId: string
): Promise<{ success: boolean; error?: string; changeOrder?: ChangeOrder; order?: Order }> {
  // SUPABASE FIRST: Get current order state from cloud
  const order = await getOrderByIdAsync(orderId)
  if (!order) return { success: false, error: 'Order not found' }

  const changeOrder = order.changeOrders?.find(co => co.id === changeOrderId)
  if (!changeOrder) return { success: false, error: 'Change order not found' }

  if (changeOrder.status !== 'approved') {
    return { success: false, error: `Change order must be approved before invoicing. Current status: ${changeOrder.status}` }
  }

  const now = new Date().toISOString()

  changeOrder.status = 'invoiced'
  changeOrder.invoiceId = invoiceId
  order.updatedAt = now

  saveOrder(order)

  // Sync to Supabase
  try {
    const result = await updateOrderSupabase(orderId, {
      changeOrders: order.changeOrders,
      updatedAt: now
    } as any)
    if (result.error) {
      log.error('Failed to sync markChangeOrderInvoiced to Supabase:', result.error)
    }
  } catch (err) {
    log.error('Supabase sync error in markChangeOrderInvoiced:', err)
  }

  return { success: true, changeOrder, order }
}

/**
 * Mark change order as paid - ready for release to partner
 */
export async function markChangeOrderPaid(
  orderId: string,
  changeOrderId: string
): Promise<{ success: boolean; error?: string; changeOrder?: ChangeOrder; order?: Order }> {
  // SUPABASE FIRST: Get current order state from cloud
  const order = await getOrderByIdAsync(orderId)
  if (!order) return { success: false, error: 'Order not found' }

  const changeOrder = order.changeOrders?.find(co => co.id === changeOrderId)
  if (!changeOrder) return { success: false, error: 'Change order not found' }

  if (changeOrder.status !== 'invoiced') {
    return { success: false, error: `Change order must be invoiced before marking paid. Current status: ${changeOrder.status}` }
  }

  const now = new Date().toISOString()

  changeOrder.status = 'paid'
  changeOrder.paidAt = now
  order.updatedAt = now

  saveOrder(order)

  // Sync to Supabase
  try {
    const result = await updateOrderSupabase(orderId, {
      changeOrders: order.changeOrders,
      updatedAt: now
    } as any)
    if (result.error) {
      log.error('Failed to sync markChangeOrderPaid to Supabase:', result.error)
    }
  } catch (err) {
    log.error('Supabase sync error in markChangeOrderPaid:', err)
  }

  return { success: true, changeOrder, order }
}

/**
 * Release change order to partner - sends the updated work order
 * Designer selects which partner receives the change order
 */
export async function releaseChangeOrderToPartner(
  orderId: string,
  changeOrderId: string,
  partnerId: string,
  partnerName: string
): Promise<{ success: boolean; error?: string; changeOrder?: ChangeOrder; order?: Order }> {
  // SUPABASE FIRST: Get current order state from cloud
  const order = await getOrderByIdAsync(orderId)
  if (!order) return { success: false, error: 'Order not found' }

  const changeOrder = order.changeOrders?.find(co => co.id === changeOrderId)
  if (!changeOrder) return { success: false, error: 'Change order not found' }

  if (changeOrder.status !== 'paid') {
    return { success: false, error: `Change order must be paid before release. Current status: ${changeOrder.status}` }
  }

  const now = new Date().toISOString()

  // Update change order
  changeOrder.status = 'released'
  changeOrder.releasedAt = now
  changeOrder.releasedToPartnerId = partnerId
  changeOrder.releasedToPartnerName = partnerName

  // Update order fab payout (add change order amount)
  const currentPayout = order.fabPayoutAmount || order.fabPayout || 0
  order.fabPayoutAmount = currentPayout + changeOrder.payoutDelta
  order.fabPayout = order.fabPayoutAmount

  // Update order status
  order.fabricationStatus = FABRICATION_STATUS.CLAIMED
  order.claimedByPartner = partnerId
  order.claimedByPartnerName = partnerName
  order.claimedByPartnerAt = now
  order.updatedAt = now

  // Add to order timeline
  if (!order.timeline) order.timeline = []
  order.timeline.push({
    event: 'change_order_released',
    timestamp: now,
    details: `Change Order ${changeOrderId} released to ${partnerName} (+$${changeOrder.payoutDelta})`
  })

  saveOrder(order)

  // Sync to Supabase
  // Check if partnerId is a valid UUID format (Supabase column expects UUID)
  const isValidUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(partnerId);
  
  try {
    const result = await updateOrderSupabase(orderId, {
      changeOrders: order.changeOrders,
      fabPayoutAmount: order.fabPayoutAmount,
      fabricationStatus: FABRICATION_STATUS.CLAIMED,
      ...(isValidUUID ? { claimedByPartner: partnerId } : {}),
      claimedByPartnerName: partnerName,
      claimedByPartnerAt: now,
      updatedAt: now
    } as any)
    if (result.error) {
      log.error('Failed to sync releaseChangeOrderToPartner to Supabase:', result.error)
    } else {
      log.info('Change order released to partner and synced:', changeOrderId, partnerId)
    }
  } catch (err) {
    log.error('Supabase sync error in releaseChangeOrderToPartner:', err)
  }

  // FULL SUITE NOTIFICATIONS - In-app, SMS, Email
  try {
    // Get partner details for contact info
    const partners = getPartners()
    const partner = partners.find((p: any) => p.id === partnerId)
    
    await notifyPartnerOfChangeOrder(
      partnerId,
      {
        name: partnerName,
        phone: partner?.phone || partner?.ownerPhone,
        email: partner?.email || partner?.ownerEmail
      },
      {
        orderId,
        changeOrderId,
        clientName: order.client?.name || 'Client',
        reason: changeOrder.reason,
        costDelta: changeOrder.costDelta,
        payoutDelta: changeOrder.payoutDelta,
        rushOrder: changeOrder.rushOrder
      }
    )
    log.info('Full suite change order notifications sent', { partnerId, changeOrderId })
  } catch (err) {
    log.error('Failed to send change order notifications:', err)
  }

  return { success: true, changeOrder, order }
}

/**
 * Get pending change orders for Finance review
 */
export function getPendingChangeOrders(): Array<{ order: Order; changeOrder: ChangeOrder }> {
  const allOrders = getAllOrders()
  const pending: Array<{ order: Order; changeOrder: ChangeOrder }> = []

  for (const order of allOrders) {
    if (order.changeOrders) {
      for (const co of order.changeOrders) {
        if (co.status === 'pending_approval') {
          pending.push({ order, changeOrder: co })
        }
      }
    }
  }

  return pending.sort((a, b) => {
    // Rush orders first
    if (a.changeOrder.rushOrder && !b.changeOrder.rushOrder) return -1
    if (!a.changeOrder.rushOrder && b.changeOrder.rushOrder) return 1
    // Then by creation date
    return new Date(a.changeOrder.createdAt).getTime() - new Date(b.changeOrder.createdAt).getTime()
  })
}

/**
 * Get change orders ready for release (paid, awaiting partner selection)
 */
export function getChangeOrdersReadyForRelease(): Array<{ order: Order; changeOrder: ChangeOrder }> {
  const allOrders = getAllOrders()
  const ready: Array<{ order: Order; changeOrder: ChangeOrder }> = []

  for (const order of allOrders) {
    if (order.changeOrders) {
      for (const co of order.changeOrders) {
        if (co.status === 'paid') {
          ready.push({ order, changeOrder: co })
        }
      }
    }
  }

  return ready.sort((a, b) => {
    // Rush orders first
    if (a.changeOrder.rushOrder && !b.changeOrder.rushOrder) return -1
    if (!a.changeOrder.rushOrder && b.changeOrder.rushOrder) return 1
    return new Date(a.changeOrder.paidAt || a.changeOrder.createdAt).getTime() - 
           new Date(b.changeOrder.paidAt || b.changeOrder.createdAt).getTime()
  })
}

/**
 * Calculate delta payout from cutlist pieces
 * Uses same payout formula as original order
 */
export function calculateDeltaPayout(deltaCutlist: DeltaCutlistPiece[]): number {
  // Base rate per piece (simplified calculation)
  // In production, this would use the same formula as getFabPayoutAmount
  const BASE_RATE_PER_PIECE = 25 // $25 per piece as baseline
  const MATERIAL_MULTIPLIER = 1.5 // Additional for materials

  let totalPayout = 0
  for (const piece of deltaCutlist) {
    totalPayout += piece.quantity * BASE_RATE_PER_PIECE * MATERIAL_MULTIPLIER
  }

  return Math.round(totalPayout)
}

/**
 * Get orders broadcast channel for cross-tab sync
 */
export function getOrdersChannel(): BroadcastChannel | null {
  // BroadcastChannel for cross-tab sync
  try {
    return new BroadcastChannel('bliss_orders_sync')
  } catch (e) {
    log.warn('BroadcastChannel not supported, cross-tab sync disabled')
    return null
  }
}

// Export default
export default {
  PRODUCTION_STAGES,
  getAllOrders,
  getOrderById,
  saveOrder,
  isOperationsVisible,
  getProductionStage,
  getProductionStageIndex,
  getProductionProgress,
  updateProductionStage,
  getOrdersByProductionStage,
  isProductionAtRisk,
  getDaysInCurrentStage,
  getClientPhaseById,
  getCompletedSubSteps,
  getCurrentTrackingPhase,
  getProductionStageById,
  getClientStageLabel,
  getInternalStageLabel,
  getProductionStageCounts,
  getProductionHealthMetrics
}

